import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabaseClient';
// Aceleasi sigle ca peste tot in aplicatie: nu se redeseneaza, se refolosesc.
import { IconRetea } from './IconRetea';
// Ecranul a fost scris direct in romana, intr-o zi. Textele stau acum in `i18n`, sectiunea
// `inboxv3`: fara engleza, submisia la Meta nu poate pleca (19 septembrie 2026).
import { t, getLocale, getLang } from './i18n';
// Aceeasi stergere ca pe pagina clientului, ca sa nu existe doua feluri de a sterge o postare.
import { stergeGrupPostari } from './PaginaClient';
import { laJpeg, urcaInDepozit } from './mediaV2';
import { TabDeLucru } from './DeLucru';

// ============================================================
// Inbox V3 — cardurile postarilor (Faza 3) si faptele pe rand (Faza 6).
// Planul: INBOX_V3_PLAN.md, §3, §13.3 si §13.8.
//
// Se construieste ALATURI de Inboxul vechi, nu in locul lui: acesta sta la `inbox3` in meniu
// si se vede doar de super_admin cat timp e in lucru. Comutarea, cand va fi gata, se face
// dintr-un singur loc — ruta `inbox` din App.js.
//
// Unitatea e POSTAREA, nu mesajul. Cifrele nu se numara in browser: ar insemna sa aducem mii
// de comentarii doar ca sa le numaram. Le calculeaza `inbox_carduri` in baza, cu drepturile
// omului care intreaba (RLS).
//
// Faptele sunt INSTANT, fara dialog de confirmare — decizia ei, 18 septembrie: „apas pe butoane
// si actiunea se intampla". Singura intrebare cu cifra ramane la teancul in bloc, cand va exista.
//
// Textele sunt in romana, direct. Trecerea prin `i18n` se face cand ecranul il inlocuieste pe
// cel vechi; pana atunci il vede un singur om.
// ============================================================

const C = {
  primar: '#1A6B3C',
  inchis: '#0F3D22',
  mentol: '#E8F5EE',
  chenar: '#D9E7DF',
  text: '#1F2A24',
  sters: '#6B7C73',
  alarma: '#B3261E',
};

export const inboxV3Vizibil = true;

// Autorul necunoscut: "Necunoscut" e un nume pus de NOI la culegere (`facebook-comments`,
// `facebook-webhook`), nu de Meta. Era singurul cuvant romanesc ramas pe ecranul in engleza,
// fiindca `c.autor || t(...)` nu prinde un sir plin. Se traduce la afisare; in baza ramane
// cum e, ca sa nu atingem datele culese.
const AUTOR_NECUNOSCUT = 'Necunoscut';
function numeAutor(nume) {
  return (!nume || nume === AUTOR_NECUNOSCUT) ? t('inboxv3', 'necunoscut') : nume;
}

function dataFrumoasa(iso) {
  if (!iso) return '';
  try {
    // Data urmeaza limba ecranului, nu tara serverului: „19 septembrie, 14:02" in romana,
    // „September 19, 02:02 PM" in engleza. Fusul ramane al Bucurestiului — clientii si paginile
    // sunt aici, iar ora trebuie sa fie cea pe care o vad ei.
    return new Intl.DateTimeFormat(getLocale(), {
      day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit',
      timeZone: 'Europe/Bucharest',
    }).format(new Date(iso));
  } catch (e) { return ''; }
}

function taieLaCuvant(text, maxim) {
  const t = (text || '').replace(/\s+/g, ' ').trim();
  if (!t) return '';
  if (t.length <= maxim) return t;
  const bucata = t.slice(0, maxim);
  const ultimulSpatiu = bucata.lastIndexOf(' ');
  return (ultimulSpatiu > maxim * 0.6 ? bucata.slice(0, ultimulSpatiu) : bucata) + '…';
}

// Eroarea unei functii Supabase vine invelita; detaliul adevarat e in corpul raspunsului.
async function detaliuEroare(error) {
  let detaliu = error && error.message ? error.message : t('inboxv3', 'eroareNecunoscuta');
  try {
    const b = await error.context.json();
    if (b) detaliu = (typeof b.detail === 'string' ? b.detail : (b.detail ? JSON.stringify(b.detail) : b.error)) || detaliu;
  } catch (e) { /* raspunsul nu era JSON */ }
  return detaliu;
}

// Cine vede stratul de AI. Decizia ei, 18 septembrie 2026: deocamdata DOAR super_admin. Clientii
// si managerii primesc Inboxul intreg, dar fara butoanele de AI — si tot asa il vede si testerul
// Meta, caruia i se da un cont de client. Cand va exista un drept per client, aici se adauga
// `permisiuni_client.poate_folosi_ai_inbox`, langa rol.
function areAI(rol) {
  return rol === 'super_admin';
}

// `poateRaspunde` vine din dreptul clientului (`raspunde_inbox`). Cand e stins, ecranul ramane
// intreg la citit, dar dispar faptele care schimba ceva PE RETEA: raspuns public, raspuns privat,
// editare, like, ascundere, stergere. Agentia (super_admin, manager) il are mereu pornit.
// Adaugat pe 20 septembrie 2026, cand clientul a primit si el Inbox V3 in locul ecranului vechi.
export default function InboxV3({ clientPreSelectat = null, sesiune = null, rol = null, poateRaspunde = true, poateBot = false, poateAnunturi = false, poateDescarca = false }) {
  // Tabul „Abonați" (botul de comentarii, etapa 6): il vad super adminul si contul de client care
  // are macar una dintre bifele botului. Managerul nu: n-are ce face acolo.
  const areTabAbonati = rol === 'super_admin' || poateBot || poateAnunturi;
  const [tab, setTab] = useState('comentarii');
  const [zile, setZile] = useState(7);
  const [carduri, setCarduri] = useState([]);
  // Stergerea unui card: doar agentia. Postarile facute de client direct pe pagina
  // (`sursa = nativ`) nu apar in lista de postari (`App.js`, filtrul `source !== 'nativ'`),
  // deci Inboxul e singurul loc din care se pot sterge. Decizia ei, 21 septembrie 2026:
  // se sterg de tot, si de pe Facebook, nu se ascund.
  const poateSterge = rol === 'super_admin' || rol === 'manager';
  async function stergeCardul(card) {
    const sterse = await stergeGrupPostari(
      [{ id: card.postare_id, retea: card.retea }],
      null,
      // Intrebarea nu e aceeasi: pe un card `nativ` scrie ca postarea e a clientului.
      { cheieConfirmare: card.sursa === 'nativ' ? 'confirmStergeNativ' : 'confirmSterge' },
    );
    if (sterse.length) setCarduri(prev => prev.filter(x => x.postare_id !== card.postare_id));
  }
  const [seIncarca, setSeIncarca] = useState(true);
  const [eroare, setEroare] = useState(null);
  const [deschisa, setDeschisa] = useState(null);
  // Filtrul cu care se deschide panoul: „propuneri” cand s-a apasat eticheta „✍ N de raspuns”.
  const [filtruPanou, setFiltruPanou] = useState('toate');

  const incarcaCarduri = useCallback(async () => {
    setSeIncarca(true);
    setEroare(null);
    const { data, error } = await supabase.rpc('inbox_carduri', {
      p_zile: zile,
      p_client_id: clientPreSelectat || null,
    });
    if (error) { setEroare(error.message); setSeIncarca(false); return; }
    setCarduri(data || []);
    setSeIncarca(false);
  }, [zile, clientPreSelectat]);

  useEffect(() => { incarcaCarduri(); }, [incarcaCarduri]);

  // „De raspuns” pe card (15 oct 2026, cerut de ea: nu stia la ce postare veche sunt ciornele).
  // Comentariile marcate de filtru „raspuns public”, inca nerezolvate, din ultimele 3 zile.
  const [deRaspuns, setDeRaspuns] = useState({});
  useEffect(() => {
    if (rol !== 'super_admin' || !carduri.length) { setDeRaspuns({}); return; }
    let activ = true;
    (async () => {
      const { data } = await supabase.from('moderare_jurnal').select('postare_id, mesaj_id')
        .eq('cuvine', 'raspuns_public').is('rezolvat_la', null)
        .gte('la', new Date(Date.now() - 3 * 86400000).toISOString())
        .in('postare_id', carduri.map(c => c.postare_id));
      const ids = (data || []).map(x => x.mesaj_id).filter(Boolean);
      const { data: m } = ids.length
        ? await supabase.from('mesaje').select('id').in('id', ids).is('raspuns_trimis_la', null).not('propunere', 'is', null)
        : { data: [] };
      const vii = new Set((m || []).map(x => x.id));
      const n = {};
      (data || []).forEach(x => { if (vii.has(x.mesaj_id)) n[x.postare_id] = (n[x.postare_id] || 0) + 1; });
      if (activ) setDeRaspuns(n);
    })();
    return () => { activ = false; };
  }, [carduri, rol]);

  // Deschiderea cardului e si vizita: de aici incolo, „nou" inseamna „de dupa clipa asta".
  // Cifra ramane pe ecran cat timp panoul e deschis, ca sa stii cu ce ai venit.
  async function deschideCardul(card) {
    setDeschisa(card);
    const utilizator = sesiune && sesiune.user ? sesiune.user.id : null;
    if (!utilizator) return;
    await supabase.from('inbox_vizite').upsert(
      { postare_id: card.postare_id, utilizator, vazut_la: new Date().toISOString() },
      { onConflict: 'postare_id,utilizator' },
    );
    // Deschiderea cardului inseamna ca le-ai vazut pe toate, deci se marcheaza citite si in
    // `mesaje`, cum fac deja tabul de mesaje private si cel de mentiuni. Insigna din meniu a iesit
    // pe 19 septembrie 2026 (cerut de ea: pe Inbox nu vrea nicio cifra), dar coloana ramane
    // adevarata — de ea atarna filtrele ecranului vechi si orice numaratoare de mai incolo.
    await supabase
      .from('mesaje')
      .update({ citit: true })
      .eq('postare_id', card.postare_id)
      .eq('citit', false);
  }

  function inchidePanoul() {
    setDeschisa(null);
    incarcaCarduri();
  }

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1180 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, flexWrap: 'wrap', marginBottom: 18 }}>
        <h1 style={{ margin: 0, fontFamily: "'DM Serif Display', Georgia, serif", fontSize: 30, color: C.inchis }}>
          Inbox
        </h1>
        <span style={{ color: C.sters, fontSize: 14 }}>
          {t('inboxv3', 'subtitlu')}
        </span>
        {/* Pe telefon cele cinci butoane nu incapeau pe un rand (429px pe un ecran de 375–390) si
            „ultimele 30 zile" ramanea in afara ecranului. Sunt doua grupuri: pe calculator stau
            unul langa altul, ca inainte; pe telefon perioada urca deasupra, pe randul ei
            (ceruta de Luminita, 12 octombrie 2026). Asezarea de telefon e in App.css. */}
        <div className="inboxv3-filtre" style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <div className="inboxv3-taburi" style={{ display: 'flex', gap: 8 }}>
          {[
            // „De lucru” (filtrul de comentarii, etapa 7, bucata 5): doar super adminul.
            ...(rol === 'super_admin' ? [['delucru', t('inboxv3', 'tabDeLucru')]] : []),
            ['comentarii', t('inboxv3', 'tabComentarii')], ['mesaje', t('inboxv3', 'tabMesaje')], ['mentiuni', t('inboxv3', 'tabMentiuni')],
            ...(areTabAbonati ? [['abonati', t('inboxv3', 'tabAbonati')]] : [])].map(([id, eticheta]) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              style={{
                border: '1px solid ' + (tab === id ? C.inchis : C.chenar),
                background: tab === id ? C.inchis : '#fff',
                color: tab === id ? '#fff' : C.text,
                borderRadius: 999, padding: '6px 14px', fontSize: 13, cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {eticheta}
            </button>
          ))}
          </div>
          {tab === 'comentarii' && <div className="inboxv3-perioada" style={{ display: 'flex', gap: 8 }}>{[7, 30].map(z => (
            <button
              key={z}
              onClick={() => setZile(z)}
              style={{
                border: '1px solid ' + (zile === z ? C.primar : C.chenar),
                background: zile === z ? C.primar : '#fff',
                color: zile === z ? '#fff' : C.text,
                borderRadius: 999, padding: '6px 14px', fontSize: 13, cursor: 'pointer',
              }}
            >
              {t('inboxv3', 'ultimeleZile', { z })}
            </button>
          ))}</div>}
        </div>
      </div>

      {eroare && (
        <div style={{ background: '#FDECEA', border: '1px solid ' + C.alarma, color: C.alarma, padding: 12, borderRadius: 8, marginBottom: 16 }}>
          {t('inboxv3', 'eroareCarduri', { eroare })}
        </div>
      )}

      {tab === 'mesaje' && <TabMesaje clientPreSelectat={clientPreSelectat} poateRaspunde={poateRaspunde} rol={rol} />}
      {tab === 'mentiuni' && <TabMentiuni clientPreSelectat={clientPreSelectat} />}
      {tab === 'delucru' && rol === 'super_admin' && <TabDeLucru clientPreSelectat={clientPreSelectat} />}
      {tab === 'abonati' && areTabAbonati && <TabAbonati clientPreSelectat={clientPreSelectat} rol={rol} poateAnunturi={poateAnunturi} poateDescarca={poateDescarca} />}

      {tab === 'comentarii' && <>
      <BandaDeVal carduri={carduri} onDeschide={deschideCardul} />

      {seIncarca && <div style={{ color: C.sters }}>{t('inboxv3', 'seIncarca')}</div>}

      {!seIncarca && !carduri.length && !eroare && (
        <div style={{ background: C.mentol, border: '1px solid ' + C.chenar, borderRadius: 12, padding: 24, color: C.text }}>
          {t('inboxv3', 'niciunCard', { zile })}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
        {carduri.map(card => (
          <Card
            key={card.postare_id}
            card={card}
            onDeschide={() => { setFiltruPanou('toate'); deschideCardul(card); }}
            onSterge={poateSterge ? () => stergeCardul(card) : null}
            deRaspuns={deRaspuns[card.postare_id] || 0}
            onDeschideCiorne={() => { setFiltruPanou('propuneri'); deschideCardul(card); }}
          />
        ))}
      </div>

      {deschisa && <PanouPostare card={deschisa} onInchide={inchidePanoul} cuAI={areAI(rol)} poateRaspunde={poateRaspunde} rol={rol} poateBot={poateBot} filtruInitial={filtruPanou} />}
      </>}
    </div>
  );
}

// ============================================================
// Mesajele private. Alt fel de card: unul per CONVERSATIE, nu per postare. Firul se deschide cu
// bule, ca intr-o aplicatie de mesagerie.
//
// Fereastra de 24 de ore: Messenger nu livreaza un mesaj al paginii daca au trecut mai mult de
// 24 de ore de la ULTIMUL mesaj al omului. Se vede pe ecran inainte sa scrii, nu dupa ce apesi.
// ============================================================

// ============================================================
// Mentiunile: cineva a pomenit pagina clientului in postarea lui. Nu sunt comentarii sub postarile
// noastre si n-au fir, deci nu incap in cardurile de postare. Erau doar in Inboxul vechi, si fara
// tabul asta s-ar fi pierdut la comutare (spus de ea, 18 septembrie).
//
// Ce se poate face cu ele: sa le citesti si sa deschizi originalul pe retea. Nu se poate raspunde
// din aplicatie — raspunsul la o mentiune inseamna un comentariu sub postarea ALTCUIVA, iar acolo
// nu avem drepturi.
//
// MENTIUNILE FARA TEXT (lamurit pe 19 septembrie 2026, pe cele doua ale lui Dan Neicu). Nu e defect
// la noi. Facebook anunta Pagina ca a fost pomenita, dar obiectul poate sta oriunde — iar cand sta
// pe un PROFIL personal, tokenul de Pagina nu-l poate citi, si atunci raman goale si textul, si
// autorul. Asa s-a si dovedit: comentariile nu erau in baza (deci pollerul nu le vedea), id-ul
// avea doua bucati in loc de trei (tipar de postare de profil, nu de Pagina), iar prima bucata nu
// era nici postare de-a noastra, nici cont conectat. Profilul lui Dan si Pagina lui se numesc la
// fel, de aceea mentiunea parea ca vine de sub o postare de-a noastra.
//
// Deci cardul gol e raspunsul corect. Ce era gresit e ce scria pe el: „(mentiune fara text)" suna
// a ceva pierdut de noi. Acum scrie ce s-a intamplat, iar butonul duce acolo unde textul se vede.
// ============================================================

// Adresa mentiunii. Culegerea o arunca pana pe 17 septembrie 2026, deci randurile de dinainte au
// coloana goala si cardul ramanea fara buton: vedeai ca cineva te-a pomenit, dar n-aveai cum sa
// ajungi acolo. `external_id` e chiar id-ul obiectului de pe Facebook si duce la el. Aceeasi plasa
// e in ecranul vechi (`normalizeazaMesaj` din App.js) — daca se schimba una, se schimba amandoua.
// Ce a facut omul, in cuvinte. `postare` tine felul obiectului asa cum il numeste Meta (vezi
// `facebook-webhook`); aici se traduce. Ce nu recunoastem cade pe fraza scurta, deci o valoare
// noua de la Meta nu poate scrie o prostie pe ecran.
const FAPTA_MENTIUNE = {
  comment: 'mentiuneComentariu',
  post: 'mentiunePostare',
  status: 'mentiunePostare',
  story: 'mentiunePostare',
  link: 'mentiunePostare',
  share: 'mentiuneDistribuire',
  photo: 'mentiunePoza',
  picture: 'mentiunePoza',
  album: 'mentiuneAlbum',
  video: 'mentiuneVideo',
};

function faptaMentiunii(m) {
  return t('inboxv3', FAPTA_MENTIUNE[(m.postare || '').trim().toLowerCase()] || 'mentiuneSimpla');
}

// Adresa unei mentiuni. Facebook NU deschide forma `facebook.com/<a>_<b>`, cea in care vin
// id-urile din webhook: intoarce „pagina indisponibila". Forma buna e `<a>/posts/<b>`.
// Se indreapta la afisare, nu in baza, ca sa mearga si mentiunile stranse pana acum
// (vazut de Luminita pe 22 septembrie 2026, apasand „Open on Facebook").
function indreaptaAdresa(u) {
  const bucati = /^https?:\/\/(?:www\.)?facebook\.com\/(\d+)_(\d+)(.*)$/.exec(u || '');
  return bucati ? `https://www.facebook.com/${bucati[1]}/posts/${bucati[2]}${bucati[3]}` : u;
}

function adresaMentiunii(m) {
  if (m.link) return indreaptaAdresa(m.link);
  if (m.retea === 'facebook' && m.external_id) {
    return indreaptaAdresa('https://www.facebook.com/' + m.external_id);
  }
  return null;
}

function TabMentiuni({ clientPreSelectat }) {
  const [mentiuni, setMentiuni] = useState([]);
  const [seIncarca, setSeIncarca] = useState(true);

  useEffect(() => {
    let anulat = false;
    (async () => {
      let q = supabase
        .from('mesaje')
        .select('id, external_id, text, autor, autor_poza, client, link, postare, scris_la, citit, retea')
        .eq('tip', 'mentiune')
        .order('scris_la', { ascending: false, nullsFirst: false })
        .limit(200);
      if (clientPreSelectat) q = q.eq('client_id', clientPreSelectat);
      const { data } = await q;
      if (anulat) return;
      setMentiuni(data || []);
      setSeIncarca(false);
      const necitite = (data || []).filter(m => !m.citit).map(m => m.id);
      if (necitite.length) await supabase.from('mesaje').update({ citit: true }).in('id', necitite);
    })();
    return () => { anulat = true; };
  }, [clientPreSelectat]);

  if (seIncarca) return <div style={{ color: C.sters }}>{t('inboxv3', 'seIncarcaMentiuni')}</div>;

  if (!mentiuni.length) {
    return (
      <div style={{ background: C.mentol, border: '1px solid ' + C.chenar, borderRadius: 12, padding: 24, color: C.text }}>
        {t('inboxv3', 'niciunaMentiune')}
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
      {mentiuni.map(m => {
        const adresa = adresaMentiunii(m);
        return (
        <div key={m.id} style={{
          background: '#fff', border: '1px solid ' + C.chenar, borderLeft: '4px solid #C9A227',
          borderRadius: 14, padding: 16, display: 'flex', flexDirection: 'column', gap: 8,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Avatar nume={numeAutor(m.autor)} poza={m.autor_poza} marime={28} />
            <span style={{ fontSize: 13, fontWeight: 600, color: C.inchis }}>{numeAutor(m.autor)}</span>
            <span style={{ marginLeft: 'auto', fontSize: 11, color: C.sters }}>{m.client}</span>
          </div>
          <div style={{ fontSize: 12, color: C.sters }}>{dataFrumoasa(m.scris_la)}</div>
          <div style={{ fontSize: 13, color: C.sters }}>{faptaMentiunii(m)}</div>
          <div style={{ fontSize: 14, color: C.text, lineHeight: 1.45, minHeight: 40, whiteSpace: 'pre-wrap' }}>
            {m.text || (
              <span style={{ color: C.sters, fontSize: 13 }}>
                {t('inboxv3', 'textDoarPeFacebook')}
              </span>
            )}
          </div>
          {adresa && (
            <a
              href={adresa} target="_blank" rel="noopener noreferrer"
              style={{
                fontSize: 13, color: C.primar, textDecoration: 'none',
                border: '1px solid ' + C.chenar, borderRadius: 999, padding: '5px 12px',
                alignSelf: 'flex-start',
              }}
            >
              {t('inboxv3', 'deschidePe', { retea: m.retea === 'instagram' ? 'Instagram' : 'Facebook' })}
            </a>
          )}
        </div>
        );
      })}
    </div>
  );
}

const ORE_FEREASTRA_MESSENGER = 24;

// Poza omului, cu initiala ca rezerva. La comentariile de Facebook ramane initiala pana la
// aprobarea Business Asset User Profile Access; pe Messenger poza vine de azi.
function Avatar({ nume, poza, marime = 36 }) {
  const initiala = (nume || '?').trim().charAt(0).toUpperCase();
  // Poza care nu se incarca NU lasa patratul spart pe ecran: cadem inapoi pe initiala.
  // Vazut pe 22 septembrie: baza tinea adresa unei poze de persoana care nu mai era in
  // depozit (404), iar conversatia arata o imagine rupta — exact pe ecranul pe care i-l
  // dam unui recenzor de la Meta.
  const [stricata, setStricata] = useState(false);
  if (poza && !stricata) {
    return (
      <img
        src={poza}
        onError={() => setStricata(true)}
        alt={nume || ''}
        style={{ width: marime, height: marime, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
      />
    );
  }
  return (
    <div style={{
      width: marime, height: marime, borderRadius: '50%', flexShrink: 0,
      background: C.mentol, color: C.primar, fontWeight: 600,
      fontSize: Math.round(marime * 0.42), lineHeight: marime + 'px', textAlign: 'center',
    }}>
      {initiala}
    </div>
  );
}

// ============================================================
// Abonatii paginii (botul de comentarii, etapa 6, bucata 4 — 14 octombrie 2026).
//
// Oamenii care au apasat cartonasul de abonare trimis de bot. De aici li se scrie: un mesaj cu
// text, optional un buton cu link, si mereu butonul „Nu mai vreau mesaje", pus de server.
//
// Hotararile ei: un singur subiect pe pagina (titlul cartonasului); cel mult un mesaj la
// `zile_intre_anunturi` zile catre acelasi om; trimite super adminul si contul de client cu bifa
// „Trimite mesaje abonatilor". Titlul, limita si textul de iesire le schimba doar super adminul.
// Lista cu nume o vede doar super adminul, pana se hotarasc regulile ei (etapa 5); ceilalti vad
// numarul.
//
// Trimiterea are doi pasi, amandoi pe server (`bot-anunt`): intai NUMARA — cati primesc, cati
// sunt sariti — apoi, cu confirmare, TRIMITE. Nimic nu pleaca dintr-o singura apasare.
// ============================================================
const BOT_MAX_TITLU_ABONARE = 65;

// Cartonasul de abonare, cum il vede omul in Messenger: poza paginii (daca are), titlul, apoi
// fraza si butonul pe care le pune Meta si pe care nu le putem schimba. Ceruta de ea pe
// 14 octombrie 2026: pusese poza si nu avea unde s-o vada inainte sa plece la cineva.
// Ce scrie Messenger pe butonul cartonasului, dupa codul ales pe pagina (probat pe viu pe
// 14 octombrie 2026; „OPT_IN” scrie „Resume updates”, de aceea lipseste din alegeri). Fara cod,
// Messenger pune „Get offers”. Hotararea ei: „Get offers” la magazine, „Get updates” la politicieni.
const BUTOANE_CARTONAS = [
  { cod: '', eticheta: 'Get offers', nota: 'abonatiButonMagazine' },
  { cod: 'GET_UPDATES', eticheta: 'Get updates', nota: 'abonatiButonPoliticieni' },
  { cod: 'GET', eticheta: 'Get messages', nota: '' },
  { cod: 'ALLOW', eticheta: 'Allow messages', nota: '' },
  { cod: 'SIGN_UP', eticheta: 'Sign up for messages', nota: '' },
];
const etichetaButonCartonas = (cod) => (BUTOANE_CARTONAS.find(b => b.cod === (cod || '')) || BUTOANE_CARTONAS[0]).eticheta;

function CartonasAbonare({ titlu, pozaUrl, pagina = '', buton = '' }) {
  return (
    <div>
      {/* Randul gri de deasupra il pune Messenger la orice cerere de abonare (vazut la alte pagini
          si la probele noastre): omul afla inainte de orice ca mesajele pot fi promotionale. */}
      <div style={{ maxWidth: 300, textAlign: 'center', fontSize: 13, color: '#65676B', marginBottom: 8 }}>
        {t('inboxv3', 'botCartonasAvertismentMeta', { pagina: pagina || t('inboxv3', 'botCartonasPagina') })}
      </div>
      <div style={{ background: '#F0F0F0', borderRadius: 18, overflow: 'hidden', maxWidth: 300, fontSize: 15, color: '#050505' }}>
        {pozaUrl && (
          <div style={{ aspectRatio: '1.91 / 1', background: '#E4E6EB' }}>
            <img src={pozaUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          </div>
        )}
        <div style={{ padding: 12 }}>
          <div style={{ fontWeight: 700, fontSize: 17, lineHeight: 1.25, marginBottom: 6 }}>{titlu || '…'}</div>
          <div style={{ color: '#65676B', fontSize: 14 }}>{t('inboxv3', 'botCartonasTextMeta')}</div>
          <div style={{ background: '#fff', borderRadius: 10, padding: '8px 10px', textAlign: 'center', fontWeight: 600, marginTop: 10 }}>
            {etichetaButonCartonas(buton)}
          </div>
        </div>
      </div>
      <div style={{ fontSize: 13, color: C.sters, marginTop: 6, maxWidth: 420 }}>{t('inboxv3', 'botCartonasExplicatie')}</div>
    </div>
  );
}

function TabAbonati({ clientPreSelectat, rol, poateAnunturi = false, poateDescarca = false }) {
  const eSuperAdmin = rol === 'super_admin';
  const poateTrimite = eSuperAdmin || poateAnunturi;
  // Etapa 5, hotararea 5 (14 octombrie 2026): contul de client vede numele pe ecran; managerii,
  // doar numerele. Descarcarea, doar cu a treia bifa pusa de ea (`poate_descarca_lista`).
  const vedeNume = eSuperAdmin || rol === 'client';
  const areDescarcare = eSuperAdmin || poateDescarca;
  const [descarc, setDescarc] = useState(false);
  async function descarca(tip) {
    if (descarc) return;
    if (!window.confirm(t('inboxv3', 'descarcaConfirm'))) return;
    setDescarc(true);
    const { data, error } = await supabase.rpc('bot_descarca_lista', { p_client: clientPreSelectat, p_tip: tip });
    setDescarc(false);
    if (error) { alert(t('inboxv3', 'nuSAPutut', { eroare: error.message })); return; }
    const randuri = Array.isArray(data) ? data : [];
    // CSV cu punct si virgula si BOM: asa il deschide Excel in romana, cu diacritice.
    const celula = v => {
      const s = v === null || v === undefined ? '' : (typeof v === 'boolean' ? (v ? 'da' : 'nu') : String(v));
      return /[";\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
    };
    const coloane = tip === 'apasari'
      ? [['nume', 'Nume'], ['intrebare', 'Întrebarea'], ['buton', 'Butonul apăsat'], ['apasat_la', 'Când'], ['abonat', 'Abonat acum']]
      : [['nume', 'Nume'], ['abonat_la', 'Abonat la'], ['ultim_anunt_la', 'Ultimul mesaj primit']];
    const text = '\ufeff' + [coloane.map(c => c[1]).join(';'), ...randuri.map(r => coloane.map(c => celula(r[c[0]])).join(';'))].join('\r\n');
    const url = URL.createObjectURL(new Blob([text], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = (tip === 'apasari' ? 'apasari-bot-' : 'abonati-') + new Date().toISOString().slice(0, 10) + '.csv';
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  }
  const [seIncarca, setSeIncarca] = useState(true);
  const [abonati, setAbonati] = useState([]);
  const [scenarii, setScenarii] = useState([]);
  const [anunturi, setAnunturi] = useState([]);
  // setarile paginii
  const [titlu, setTitlu] = useState('');
  const [zile, setZile] = useState(7);
  const [textIesire, setTextIesire] = useState('');
  const [linkConf, setLinkConf] = useState('');
  const [textConfirmare, setTextConfirmare] = useState('');
  // Poza de pe cartonasul de abonare (ceruta de ea, 14 octombrie 2026: „la Axinia e si poza").
  // Urca in depozitul clientului, ca orice poza de postare, si se tine ca adresa publica.
  const [pozaUrl, setPozaUrl] = useState('');
  const [seUrcaPoza, setSeUrcaPoza] = useState(false);
  const [butonCartonas, setButonCartonas] = useState('');
  const [setariSalvate, setSetariSalvate] = useState(false);
  // mesajul
  const [text, setText] = useState('');
  const [butonTitlu, setButonTitlu] = useState('');
  const [butonLink, setButonLink] = useState('');
  const [segment, setSegment] = useState('toti');
  const [numaratoare, setNumaratoare] = useState(null);
  const [ocupat, setOcupat] = useState(false);
  const [rezultat, setRezultat] = useState(null);

  const incarca = useCallback(async () => {
    if (!clientPreSelectat) { setSeIncarca(false); return; }
    setSeIncarca(true);
    const [{ data: ab }, { data: st }, { data: sc }, { data: an }] = await Promise.all([
      supabase.from('bot_abonati').select('id, nume, stare, abonat_la, ultim_anunt_la')
        .eq('client_id', clientPreSelectat).order('abonat_la', { ascending: false }).limit(2000),
      supabase.from('bot_setari').select('abonare_titlu, zile_intre_anunturi, text_iesire, abonare_poza_url, abonare_buton, link_confidentialitate, text_confirmare')
        .eq('client_id', clientPreSelectat).maybeSingle(),
      supabase.from('bot_scenarii').select('id, intrebare, butoane, doar_numere, tema')
        .eq('client_id', clientPreSelectat).order('id', { ascending: false }),
      supabase.from('bot_anunturi').select('id, text, buton_titlu, segment, stare, creat_la, destinatari, trimise, esuate, sarite')
        .eq('client_id', clientPreSelectat).order('creat_la', { ascending: false }).limit(20),
    ]);
    setAbonati(ab || []);
    setScenarii(sc || []);
    setAnunturi(an || []);
    setTitlu(st && st.abonare_titlu ? st.abonare_titlu : '');
    setZile(st && st.zile_intre_anunturi ? st.zile_intre_anunturi : 7);
    setTextIesire(st && st.text_iesire ? st.text_iesire : '');
    setLinkConf(st && st.link_confidentialitate ? st.link_confidentialitate : '');
    setTextConfirmare(st && st.text_confirmare ? st.text_confirmare : '');
    setPozaUrl(st && st.abonare_poza_url ? st.abonare_poza_url : '');
    setButonCartonas(st && st.abonare_buton ? st.abonare_buton : '');
    setSeIncarca(false);
  }, [clientPreSelectat]);

  useEffect(() => { incarca(); }, [incarca]);

  // Orice schimbare a mesajului sau a segmentului strica numaratoarea: se numara din nou.
  useEffect(() => { setNumaratoare(null); setRezultat(null); }, [text, butonTitlu, butonLink, segment]);

  if (!clientPreSelectat) {
    return (
      <div style={{ background: C.mentol, border: '1px solid ' + C.chenar, borderRadius: 12, padding: 24, color: C.text }}>
        {t('inboxv3', 'abonatiAlegeClient')}
      </div>
    );
  }
  if (seIncarca) return <div style={{ color: C.sters }}>{t('inboxv3', 'seIncarca')}</div>;

  const activi = abonati.filter(a => a.stare === 'abonat');
  const plecati = abonati.length - activi.length;

  const segmentCorp = () => {
    if (segment === 'toti') return { tip: 'toti' };
    if (segment.startsWith('tema:')) return { tip: 'tema', tema: segment.slice(5) };
    if (segment.startsWith('postare:')) return { tip: 'scenariu', scenariu_id: Number(segment.slice(8)) };
    const [scenariuId, ...rest] = segment.split(':');
    return { tip: 'buton', scenariu_id: Number(scenariuId), cod: rest.join(':') };
  };
  const corpMesaj = () => ({
    client_id: clientPreSelectat,
    text: text.trim(),
    buton_titlu: butonTitlu.trim(),
    buton_link: butonLink.trim(),
    segment: segmentCorp(),
  });

  const problema =
    !text.trim() ? 'abonatiLipsaText'
    : text.trim().length > BOT_MAX_INTREBARE ? 'abonatiTextLung'
    : !!butonTitlu.trim() !== !!butonLink.trim() ? 'abonatiButonIncomplet'
    : butonTitlu.trim().length > BOT_MAX_TITLU ? 'botTitluLung'
    : butonLink.trim() && !/^https:\/\/\S+$/i.test(butonLink.trim()) ? 'abonatiLinkGresit'
    : null;

  async function numara() {
    if (ocupat || problema) return;
    setOcupat(true);
    const { data, error } = await supabase.functions.invoke('bot-anunt', { body: { ...corpMesaj(), mod: 'numara' } });
    setOcupat(false);
    if (error) { alert(t('inboxv3', 'nuSAPutut', { eroare: await detaliuEroare(error) })); return; }
    setNumaratoare(data);
  }

  async function trimite() {
    if (ocupat || problema || !numaratoare || !numaratoare.de_trimis) return;
    if (!window.confirm(t('inboxv3', 'abonatiConfirmTrimitere', { n: numaratoare.de_trimis, text: text.trim() }))) return;
    setOcupat(true);
    let anuntId = null;
    let ultim = null;
    // Serverul lucreaza in transe; se cheama din nou, cu acelasi anunt, pana nu mai ramane nimeni.
    for (let tura = 0; tura < 400; tura++) {
      const { data, error } = await supabase.functions.invoke('bot-anunt', {
        body: { ...corpMesaj(), mod: 'trimite', confirmat: true, ...(anuntId ? { anunt_id: anuntId } : {}) },
      });
      if (error) {
        setOcupat(false);
        alert(t('inboxv3', 'nuSAPutut', { eroare: await detaliuEroare(error) }));
        await incarca();
        return;
      }
      ultim = data;
      anuntId = data.anunt_id;
      if (!data.ramase) break;
    }
    setOcupat(false);
    setRezultat(ultim);
    setText(''); setButonTitlu(''); setButonLink(''); setSegment('toti');
    await incarca();
  }

  async function alegePoza(e) {
    const fisier = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!fisier) return;
    if (!/^image\//.test(fisier.type)) { alert(t('inboxv3', 'abonatiPozaDoarImagine')); return; }
    setSeUrcaPoza(true);
    try {
      // Messenger vrea JPEG sau PNG; trecerea prin `laJpeg` micsoreaza si pozele de telefon.
      // `laJpeg` intoarce un obiect, nu fisierul gol: `{ fisier, convertit, micsorat }`. Dat intreg
      // mai departe, urcarea cadea cu „reading 'split'" (14 octombrie 2026, vazut de ea pe ecran).
      const { fisier: jpeg } = await laJpeg(fisier);
      const { url } = await urcaInDepozit(clientPreSelectat, jpeg);
      setPozaUrl(url);
      setSetariSalvate(false);
    } catch (err) {
      alert(t('inboxv3', 'nuSAPutut', { eroare: err && err.message ? err.message : String(err) }));
    } finally {
      setSeUrcaPoza(false);
    }
  }

  async function salveazaSetarile() {
    if (ocupat) return;
    if (titlu.trim().length > BOT_MAX_TITLU_ABONARE) return;
    if (linkConf.trim() && !/^https:\/\/\S+$/.test(linkConf.trim())) { alert(t('inboxv3', 'abonatiLinkGresit')); return; }
    setOcupat(true);
    const { error } = await supabase.from('bot_setari').upsert({
      client_id: clientPreSelectat,
      abonare_titlu: titlu.trim() || null,
      zile_intre_anunturi: Math.min(365, Math.max(1, Number(zile) || 7)),
      text_iesire: textIesire.trim() || null,
      link_confidentialitate: linkConf.trim() || null,
      text_confirmare: textConfirmare.trim() || null,
      abonare_poza_url: pozaUrl || null,
      abonare_buton: butonCartonas || null,
      actualizat_la: new Date().toISOString(),
    }, { onConflict: 'client_id' });
    setOcupat(false);
    if (error) { alert(t('inboxv3', 'nuSAPutut', { eroare: error.message })); return; }
    setSetariSalvate(true);
    await incarca();
  }

  const camp = { width: '100%', boxSizing: 'border-box', border: '1px solid ' + C.chenar, borderRadius: 8, padding: '8px 10px', fontSize: 14, fontFamily: 'inherit', color: C.text };
  const eticheta = { fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 4 };
  const cutie = { background: '#fff', border: '1px solid ' + C.chenar, borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 12 };
  const numar = (n, max) => <span style={{ fontSize: 12, color: n > max ? C.alarma : C.sters }}>{n}/{max}</span>;
  const numeSegment = (s) => {
    if (s && s.tip === 'tema') return t('inboxv3', 'abonatiSegmentTema', { tema: s.tema });
    if (s && s.tip === 'scenariu') {
      const sc0 = scenarii.find(x => x.id === s.scenariu_id);
      return t('inboxv3', 'abonatiSegmentPostare', { intrebare: sc0 ? taieLaCuvant(sc0.intrebare, 50) : '#' + s.scenariu_id });
    }
    if (!s || s.tip !== 'buton') return t('inboxv3', 'abonatiSegmentToti');
    const sc = scenarii.find(x => x.id === s.scenariu_id);
    const b = sc && Array.isArray(sc.butoane) ? sc.butoane.find(y => y.cod === s.cod) : null;
    return t('inboxv3', 'abonatiSegmentButon', { buton: b ? b.titlu : s.cod, intrebare: sc ? taieLaCuvant(sc.intrebare, 50) : '#' + s.scenariu_id });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 760 }}>
      <div style={cutie}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: 34, color: C.inchis, lineHeight: 1 }}>{activi.length}</span>
          <span style={{ fontSize: 15, color: C.text }}>{t('inboxv3', activi.length === 1 ? 'abonatiNumar1' : 'abonatiNumar')}</span>
          {plecati > 0 && <span style={{ fontSize: 13, color: C.sters }}>{t('inboxv3', 'abonatiPlecati', { n: plecati })}</span>}
        </div>
        <div style={{ fontSize: 13, color: C.text }}>
          {titlu
            ? t('inboxv3', 'abonatiSubiect', { titlu })
            : t('inboxv3', 'abonatiFaraSubiect')}
        </div>
        {/* Cartonasul, cu titlul si poza de ACUM din setarile de mai jos — se vede si inainte de „Salveaza". */}
        {titlu && <CartonasAbonare titlu={titlu} pozaUrl={pozaUrl} buton={butonCartonas} />}
        {titlu && !linkConf.trim() && (
          <div style={{ fontSize: 13, color: C.alarma }}>{t('inboxv3', 'abonatiFaraLink')}</div>
        )}
        {vedeNume && activi.length > 0 && (
          <div style={{ fontSize: 13, color: C.text, maxHeight: 180, overflowY: 'auto', borderTop: '1px solid ' + C.chenar, paddingTop: 10 }}>
            {activi.map(a => (
              <div key={a.id}>
                {a.nume || t('inboxv3', 'botOmNecunoscut')}
                <span style={{ color: C.sters }}> · {dataFrumoasa(a.abonat_la)}</span>
              </div>
            ))}
          </div>
        )}
        {areDescarcare && (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', borderTop: '1px solid ' + C.chenar, paddingTop: 10 }}>
            <button type="button" onClick={() => descarca('abonati')} disabled={descarc}
              style={{ border: '1px solid ' + C.chenar, background: '#fff', borderRadius: 8, padding: '6px 12px', fontSize: 13, cursor: descarc ? 'default' : 'pointer' }}>
              {t('inboxv3', 'descarcaAbonati')}
            </button>
            <button type="button" onClick={() => descarca('apasari')} disabled={descarc}
              style={{ border: '1px solid ' + C.chenar, background: '#fff', borderRadius: 8, padding: '6px 12px', fontSize: 13, cursor: descarc ? 'default' : 'pointer' }}>
              {t('inboxv3', 'descarcaApasari')}
            </button>
            <span style={{ fontSize: 12, color: C.sters }}>{t('inboxv3', 'descarcaNota')}</span>
          </div>
        )}
      </div>

      {poateTrimite && (
        <div style={cutie}>
          <div style={{ fontWeight: 600, color: C.inchis, fontSize: 15 }}>{t('inboxv3', 'abonatiMesajNou')}</div>

          <div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <div style={eticheta}>{t('inboxv3', 'abonatiCampText')}</div>
              <span style={{ marginLeft: 'auto' }}>{numar(text.trim().length, BOT_MAX_INTREBARE)}</span>
            </div>
            <textarea value={text} onChange={e => setText(e.target.value)} rows={4}
              placeholder={t('inboxv3', 'abonatiTextExemplu')} style={{ ...camp, resize: 'vertical' }} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 2fr)', gap: 10 }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <div style={eticheta}>{t('inboxv3', 'abonatiCampButon')}</div>
                <span style={{ marginLeft: 'auto' }}>{numar(butonTitlu.trim().length, BOT_MAX_TITLU)}</span>
              </div>
              <input value={butonTitlu} onChange={e => setButonTitlu(e.target.value)}
                placeholder={t('inboxv3', 'abonatiButonExemplu')} style={camp} />
            </div>
            <div>
              <div style={eticheta}>{t('inboxv3', 'abonatiCampLink')}</div>
              <input value={butonLink} onChange={e => setButonLink(e.target.value)} placeholder="https://" style={camp} />
            </div>
          </div>

          <div>
            <div style={eticheta}>{t('inboxv3', 'abonatiCampCui')}</div>
            <select value={segment} onChange={e => setSegment(e.target.value)} style={camp}>
              <option value="toti">{t('inboxv3', 'abonatiSegmentToti')}</option>
              {/* Pe tema si pe postare (15 oct 2026): cei veniti de acolo sau carora botul le-a scris acolo. */}
              {[...new Set(scenarii.map(sc => sc.tema).filter(Boolean))].sort().map(tm => (
                <option key={'tema:' + tm} value={'tema:' + tm}>{t('inboxv3', 'abonatiSegmentTema', { tema: tm })}</option>
              ))}
              {scenarii.map(sc => (
                <option key={'postare:' + sc.id} value={'postare:' + sc.id}>{t('inboxv3', 'abonatiSegmentPostare', { intrebare: taieLaCuvant(sc.intrebare, 50) })}</option>
              ))}
              {/* La intrebarile de opinie nu se stie cine ce a apasat, deci nu exista segment pe buton. */}
              {scenarii.filter(sc => !sc.doar_numere).map(sc => (Array.isArray(sc.butoane) ? sc.butoane : []).map(b => (
                <option key={sc.id + ':' + b.cod} value={sc.id + ':' + b.cod}>
                  {t('inboxv3', 'abonatiSegmentButon', { buton: b.titlu, intrebare: taieLaCuvant(sc.intrebare, 50) })}
                </option>
              )))}
            </select>
          </div>

          {/* Cum vede omul mesajul. Butonul de iesire e pus de server la fiecare mesaj. */}
          <div>
            <div style={eticheta}>{t('inboxv3', 'botPrevizualizare')}</div>
            <div style={{ background: '#F0F2F5', borderRadius: 18, padding: 12, maxWidth: 300, fontSize: 15, color: '#050505' }}>
              <div style={{ whiteSpace: 'pre-wrap', marginBottom: 10 }}>{text.trim() || '…'}</div>
              {butonTitlu.trim() && (
                <div style={{ background: '#fff', borderRadius: 10, padding: '8px 10px', textAlign: 'center', fontWeight: 600, marginTop: 6 }}>{butonTitlu.trim()}</div>
              )}
              <div style={{ background: '#fff', borderRadius: 10, padding: '8px 10px', textAlign: 'center', fontWeight: 600, marginTop: 6 }}>
                {t('inboxv3', 'abonatiButonIesire')}
              </div>
            </div>
            <div style={{ fontSize: 13, color: C.sters, marginTop: 6 }}>{t('inboxv3', 'abonatiIesireExplicatie')}</div>
          </div>

          {problema && text.trim() !== '' && <div style={{ color: C.alarma, fontSize: 13 }}>{t('inboxv3', problema)}</div>}

          {numaratoare && (
            <div style={{ background: C.mentol, borderRadius: 8, padding: '10px 12px', fontSize: 14, color: C.text }}>
              <div><strong>{t('inboxv3', 'abonatiVorPrimi', { n: numaratoare.de_trimis })}</strong></div>
              <div style={{ fontSize: 13, marginTop: 4 }}>
                {t('inboxv3', 'abonatiNumaratoare', { segment: numaratoare.in_segment, sariti: numaratoare.sariti_prea_devreme, zile: numaratoare.zile_intre_anunturi })}
              </div>
              {numaratoare.oprit && <div style={{ color: C.alarma, marginTop: 4 }}>{t('inboxv3', 'botOpritPePagina')}</div>}
            </div>
          )}

          {rezultat && (
            <div style={{ background: C.mentol, borderRadius: 8, padding: '10px 12px', fontSize: 14, color: C.text }}>
              {t('inboxv3', 'abonatiRezultat', { trimise: rezultat.trimise, esuate: rezultat.esuate })}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <button type="button" onClick={numara} disabled={ocupat || !!problema}
              style={{ border: '1px solid ' + C.primar, background: '#fff', color: C.primar, borderRadius: 8, padding: '9px 16px', fontSize: 14, fontWeight: 600, cursor: (ocupat || problema) ? 'default' : 'pointer', opacity: (ocupat || problema) ? 0.5 : 1 }}>
              {t('inboxv3', 'abonatiNumara')}
            </button>
            <button type="button" onClick={trimite} disabled={ocupat || !!problema || !numaratoare || !numaratoare.de_trimis || numaratoare.oprit}
              style={{ border: 'none', background: C.primar, color: '#fff', borderRadius: 8, padding: '9px 16px', fontSize: 14, fontWeight: 600,
                cursor: (ocupat || problema || !numaratoare || !numaratoare.de_trimis) ? 'default' : 'pointer',
                opacity: (ocupat || problema || !numaratoare || !numaratoare.de_trimis || numaratoare.oprit) ? 0.5 : 1 }}>
              {ocupat ? t('inboxv3', 'abonatiSeLucreaza') : t('inboxv3', 'abonatiTrimite')}
            </button>
            {!numaratoare && <span style={{ fontSize: 13, color: C.sters }}>{t('inboxv3', 'abonatiIntaiNumara')}</span>}
          </div>
        </div>
      )}

      {anunturi.length > 0 && (
        <div style={cutie}>
          <div style={{ fontWeight: 600, color: C.inchis, fontSize: 15 }}>{t('inboxv3', 'abonatiIstoric')}</div>
          {anunturi.map(a => (
            <div key={a.id} style={{ borderTop: '1px solid ' + C.chenar, paddingTop: 10, fontSize: 13, color: C.text }}>
              <div style={{ color: C.sters }}>{dataFrumoasa(a.creat_la)} · {numeSegment(a.segment)}</div>
              <div style={{ whiteSpace: 'pre-wrap', margin: '4px 0' }}>{a.text}</div>
              <div>
                {t('inboxv3', 'abonatiIstoricCifre', { trimise: a.trimise, esuate: a.esuate, sarite: a.sarite })}
                {a.stare !== 'trimis' && <span style={{ color: C.alarma }}> · {t('inboxv3', 'abonatiNeterminat')}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {eSuperAdmin && (
        <div style={cutie}>
          <div style={{ fontWeight: 600, color: C.inchis, fontSize: 15 }}>{t('inboxv3', 'abonatiSetari')}</div>
          <div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <div style={eticheta}>{t('inboxv3', 'abonatiCampTitlu')}</div>
              <span style={{ marginLeft: 'auto' }}>{numar(titlu.trim().length, BOT_MAX_TITLU_ABONARE)}</span>
            </div>
            <input value={titlu} onChange={e => { setTitlu(e.target.value); setSetariSalvate(false); }}
              placeholder={t('inboxv3', 'abonatiTitluExemplu')} style={camp} />
            <div style={{ fontSize: 13, color: C.sters, marginTop: 4 }}>{t('inboxv3', 'abonatiTitluExplicatie')}</div>
          </div>
          <div>
            <div style={eticheta}>{t('inboxv3', 'abonatiCampZile')}</div>
            <input type="number" min={1} max={365} value={zile}
              onChange={e => { setZile(e.target.value); setSetariSalvate(false); }} style={{ ...camp, maxWidth: 120 }} />
          </div>
          <div>
            <div style={eticheta}>{t('inboxv3', 'abonatiCampButonCartonas')}</div>
            <select value={butonCartonas} onChange={e => { setButonCartonas(e.target.value); setSetariSalvate(false); }} style={{ ...camp, maxWidth: 360 }}>
              {BUTOANE_CARTONAS.map(b => (
                <option key={b.cod || 'implicit'} value={b.cod}>
                  {b.eticheta}{b.nota ? ' — ' + t('inboxv3', b.nota) : ''}
                </option>
              ))}
            </select>
            <div style={{ fontSize: 13, color: C.sters, marginTop: 4 }}>{t('inboxv3', 'abonatiButonCartonasExplicatie')}</div>
          </div>
          <div>
            <div style={eticheta}>{t('inboxv3', 'abonatiCampPoza')}</div>
            {pozaUrl && (
              <div style={{ marginBottom: 8 }}>
                {/* Messenger o taie lat, cam 1,91 la 1: asa se vede si aici. */}
                <div style={{ width: 300, maxWidth: '100%', aspectRatio: '1.91 / 1', borderRadius: 12, overflow: 'hidden', background: '#F0F2F5' }}>
                  <img src={pozaUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <label style={{ border: '1px solid ' + C.chenar, borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600, color: C.text, cursor: seUrcaPoza ? 'default' : 'pointer', opacity: seUrcaPoza ? 0.6 : 1 }}>
                {seUrcaPoza ? t('inboxv3', 'abonatiSeLucreaza') : (pozaUrl ? t('inboxv3', 'abonatiPozaSchimba') : t('inboxv3', 'abonatiPozaAlege'))}
                <input type="file" accept="image/*" onChange={alegePoza} disabled={seUrcaPoza} style={{ display: 'none' }} />
              </label>
              {pozaUrl && (
                <button type="button" onClick={() => { setPozaUrl(''); setSetariSalvate(false); }}
                  style={{ border: 'none', background: 'transparent', color: C.alarma, cursor: 'pointer', fontSize: 13 }}>
                  {t('inboxv3', 'abonatiPozaScoate')}
                </button>
              )}
            </div>
            <div style={{ fontSize: 13, color: C.sters, marginTop: 4 }}>{t('inboxv3', 'abonatiPozaExplicatie')}</div>
          </div>
          <div>
            <div style={eticheta}>{t('inboxv3', 'abonatiCampLinkConf')}</div>
            <input value={linkConf} onChange={e => { setLinkConf(e.target.value); setSetariSalvate(false); }}
              placeholder="https://" style={camp} />
            <div style={{ fontSize: 13, color: C.sters, marginTop: 4 }}>{t('inboxv3', 'abonatiLinkConfExplicatie')}</div>
          </div>
          <div>
            <div style={eticheta}>{t('inboxv3', 'abonatiCampConfirmare')}</div>
            <textarea value={textConfirmare} onChange={e => { setTextConfirmare(e.target.value); setSetariSalvate(false); }} rows={2}
              placeholder={t('inboxv3', 'abonatiConfirmareDinOficiu')} style={{ ...camp, resize: 'vertical' }} />
            {/* Cum vede omul confirmarea, imediat dupa ce apasa cartonasul. */}
            <div style={{ background: '#F0F2F5', borderRadius: 18, padding: 12, maxWidth: 300, fontSize: 15, color: '#050505', marginTop: 8 }}>
              <div style={{ whiteSpace: 'pre-wrap' }}>{textConfirmare.trim() || t('inboxv3', 'abonatiConfirmareDinOficiu')}</div>
              {/* Doar text: Messenger face singur adresa albastra si apasabila. */}
              {linkConf.trim() && (
                <div style={{ marginTop: 12, wordBreak: 'break-all' }}>
                  {t('inboxv3', 'abonatiPoliticaConf')}: <span style={{ color: '#0866FF', textDecoration: 'underline' }}>{linkConf.trim()}</span>
                </div>
              )}
            </div>
          </div>
          <div>
            <div style={eticheta}>{t('inboxv3', 'abonatiCampIesire')}</div>
            <textarea value={textIesire} onChange={e => { setTextIesire(e.target.value); setSetariSalvate(false); }} rows={2}
              placeholder={t('inboxv3', 'abonatiIesireDinOficiu')} style={{ ...camp, resize: 'vertical' }} />
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <button type="button" onClick={salveazaSetarile} disabled={ocupat || titlu.trim().length > BOT_MAX_TITLU_ABONARE}
              style={{ border: 'none', background: C.primar, color: '#fff', borderRadius: 8, padding: '9px 16px', fontSize: 14, fontWeight: 600, cursor: ocupat ? 'default' : 'pointer', opacity: ocupat ? 0.5 : 1 }}>
              {t('inboxv3', 'botSalveaza')}
            </button>
            {setariSalvate && <span style={{ fontSize: 13, color: C.primar }}>{t('inboxv3', 'abonatiSetariSalvate')}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

function TabMesaje({ clientPreSelectat, poateRaspunde = true, rol = null }) {
  const [conversatii, setConversatii] = useState([]);
  const [seIncarca, setSeIncarca] = useState(true);
  const [eroare, setEroare] = useState(null);
  const [deschisa, setDeschisa] = useState(null);

  const incarca = useCallback(async () => {
    setSeIncarca(true);
    setEroare(null);
    const { data, error } = await supabase.rpc('inbox_conversatii', {
      p_client_id: clientPreSelectat || null,
    });
    if (error) { setEroare(error.message); setSeIncarca(false); return; }
    setConversatii(data || []);
    setSeIncarca(false);
  }, [clientPreSelectat]);

  useEffect(() => { incarca(); }, [incarca]);

  if (seIncarca) return <div style={{ color: C.sters }}>{t('inboxv3', 'seIncarcaConversatii')}</div>;

  if (eroare) {
    return (
      <div style={{ background: '#FDECEA', border: '1px solid ' + C.alarma, color: C.alarma, padding: 12, borderRadius: 8 }}>
        {t('inboxv3', 'eroareConversatii', { eroare })}
      </div>
    );
  }

  if (!conversatii.length) {
    return (
      <div style={{ background: C.mentol, border: '1px solid ' + C.chenar, borderRadius: 12, padding: 24, color: C.text }}>
        {t('inboxv3', 'niciunaConversatie')}
      </div>
    );
  }

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
        {conversatii.map(cv => (
          <CardConversatie key={cv.client_id + ':' + cv.conversatie_cu} cv={cv} onDeschide={() => setDeschisa(cv)} />
        ))}
      </div>
      {deschisa && (
        <PanouConversatie cv={deschisa} onInchide={() => { setDeschisa(null); incarca(); }} poateRaspunde={poateRaspunde} rol={rol} />
      )}
    </>
  );
}

function CardConversatie({ cv, onDeschide }) {
  const necitite = Number(cv.necitite || 0);
  return (
    <button
      onClick={onDeschide}
      style={{
        textAlign: 'left', cursor: 'pointer', background: '#fff',
        border: '1px solid ' + (necitite > 0 ? C.primar : C.chenar),
        borderLeft: '4px solid #7A6FD6',
        borderRadius: 14, padding: 16, display: 'flex', flexDirection: 'column', gap: 8,
        font: 'inherit',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Avatar nume={cv.persoana} poza={cv.poza} marime={34} />
        <span style={{ fontSize: 13, fontWeight: 600, color: C.inchis }}>{cv.persoana}</span>
        {necitite > 0 && (
          <span style={{ background: C.primar, color: '#fff', borderRadius: 999, padding: '1px 8px', fontSize: 11 }}>
            {necitite}
          </span>
        )}
        {/* Sigla retelei pe cardul de conversatie (23 septembrie 2026). Pe lista se vedea
            doar numele omului, deci nu se putea spune de unde vine mesajul — iar lista
            tine de acum si Instagram, cand vine aprobarea. Cerut de Luminita. */}
        <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, color: C.sters }}>
          <IconRetea id={cv.retea === 'instagram' ? 'instagram' : 'facebook'} size={12} />
          {cv.retea === 'instagram' ? 'Instagram' : 'Messenger'} · {cv.client_nume}
        </span>
      </div>

      <div style={{ fontSize: 12, color: C.sters }}>{dataFrumoasa(cv.ultimul_la)}</div>

      <div style={{ fontSize: 14, color: C.text, lineHeight: 1.45, minHeight: 40 }}>
        {cv.ultimul_e_al_nostru && <span style={{ color: C.primar }}>{t('inboxv3', 'tu')}</span>}
        {taieLaCuvant(cv.ultimul_text, 110) || <span style={{ color: C.sters }}>{t('inboxv3', 'mesajFaraText')}</span>}
      </div>

      <div style={{ fontSize: 11, color: C.sters, borderTop: '1px solid ' + C.chenar, paddingTop: 8 }}>
        {t('inboxv3', 'mesajeInFir', { n: cv.total })}
      </div>
    </button>
  );
}

function PanouConversatie({ cv, onInchide, poateRaspunde = true, rol = null }) {
  const [mesaje, setMesaje] = useState([]);
  const [sterg, setSterg] = useState(false);
  // „Sterge tot despre acest om” (etapa 5 a botului, 14 octombrie 2026): pentru cererile de
  // stergere a datelor. Il au super adminul si contul de client; managerul, nu (hotararea ei).
  const poateStergeOmul = rol === 'super_admin' || rol === 'client';
  async function stergeOmul() {
    if (sterg) return;
    if (!window.confirm(t('inboxv3', 'stergeOmulConfirm', { nume: cv.persoana }))) return;
    setSterg(true);
    try {
      const { data, error } = await supabase.functions.invoke('bot-sterge-om', {
        body: { client_id: cv.client_id, psid: cv.conversatie_cu },
      });
      if (error) throw new Error(await detaliuEroare(error));
      alert(t('inboxv3', 'stergeOmulGata', { mesaje: data.mesaje || 0, apasari: data.apasari || 0 }));
      onInchide();
    } catch (err) {
      alert(t('inboxv3', 'nuSAPutut', { eroare: err.message }));
      setSterg(false);
    }
  }
  const [seIncarca, setSeIncarca] = useState(true);
  const [text, setText] = useState('');
  const [trimit, setTrimit] = useState(false);

  const incarcaFirul = useCallback(async () => {
    const { data } = await supabase
      .from('mesaje')
      .select('id, text, autor, al_paginii, scris_la, citit, media_url')
      .eq('tip', 'mesaj')
      .eq('client_id', cv.client_id)
      .eq('conversatie_cu', cv.conversatie_cu)
      .order('scris_la', { ascending: true })
      .limit(300);
    setMesaje(data || []);
    setSeIncarca(false);

    // Deschiderea firului inseamna citit. Doar mesajele LUI: ale noastre sunt citite din start.
    const necitite = (data || []).filter(m => !m.citit && !m.al_paginii).map(m => m.id);
    if (necitite.length) await supabase.from('mesaje').update({ citit: true }).in('id', necitite);
  }, [cv.client_id, cv.conversatie_cu]);

  useEffect(() => { incarcaFirul(); }, [incarcaFirul]);

  // Ultimul mesaj PRIMIT da doua lucruri: dreptul de a raspunde (fereastra de 24 de ore) si
  // randul pe care `facebook-message-send` il foloseste ca sa afle catre cine trimite.
  const ultimulPrimit = [...mesaje].reverse().find(m => !m.al_paginii);
  const oreDeLaUltimul = ultimulPrimit && ultimulPrimit.scris_la
    ? (Date.now() - new Date(ultimulPrimit.scris_la).getTime()) / 3600000
    : Infinity;
  const inFereastra = oreDeLaUltimul <= ORE_FEREASTRA_MESSENGER;

  async function trimite() {
    if (!text.trim() || !ultimulPrimit) return;
    setTrimit(true);
    try {
      const { error } = await supabase.functions.invoke('facebook-message-send', {
        body: { mesaj_id: ultimulPrimit.id, raspuns_text: text },
      });
      if (error) throw new Error(await detaliuEroare(error));
      setText('');
      await incarcaFirul();
    } catch (err) {
      alert(t('inboxv3', 'mesajNeplecat', { eroare: err.message }));
    } finally {
      setTrimit(false);
    }
  }

  return (
    <div
      onClick={onInchide}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,61,34,0.35)', display: 'flex', justifyContent: 'flex-end', zIndex: 60 }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ width: 'min(600px, 100%)', background: '#fff', height: '100%', display: 'flex', flexDirection: 'column' }}
      >
        <div style={{ padding: '18px 22px 12px', borderBottom: '1px solid ' + C.chenar, display: 'flex', alignItems: 'center', gap: 12 }}>
          <Avatar nume={cv.persoana} poza={cv.poza} marime={42} />
          <div>
            <div style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: 20, color: C.inchis }}>{cv.persoana}</div>
            {/* Reteaua adevarata a conversatiei (23 septembrie 2026). Pana azi scria
                „Messenger" pe orice, fiindca lista continea doar Facebook. Acum
                `inbox_conversatii` intoarce si Instagram, deci ecranul trebuie sa spuna
                care e care — inclusiv in filmul pentru `instagram_manage_messages`. */}
            <div style={{ fontSize: 12, color: C.sters }}>{cv.retea === 'instagram' ? 'Instagram' : 'Messenger'} · {cv.client_nume}</div>
            {poateStergeOmul && (
              <button type="button" onClick={stergeOmul} disabled={sterg}
                style={{ border: 'none', background: 'transparent', color: C.alarma, cursor: sterg ? 'default' : 'pointer', fontSize: 12, padding: 0, marginTop: 4 }}>
                {sterg ? t('inboxv3', 'stergeOmulSeLucreaza') : t('inboxv3', 'stergeOmul')}
              </button>
            )}
          </div>
          <button onClick={onInchide} style={{ marginLeft: 'auto', border: 'none', background: 'transparent', fontSize: 22, cursor: 'pointer', color: C.sters }}>×</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 22px', background: '#FAFBFA' }}>
          {seIncarca && <div style={{ color: C.sters }}>{t('inboxv3', 'seIncarcaFirul')}</div>}
          {mesaje.map(m => (
            <div key={m.id} style={{ display: 'flex', justifyContent: m.al_paginii ? 'flex-end' : 'flex-start', marginBottom: 8 }}>
              <div style={{
                maxWidth: '78%', padding: '8px 12px', borderRadius: 14,
                background: m.al_paginii ? C.primar : '#fff',
                color: m.al_paginii ? '#fff' : C.text,
                border: m.al_paginii ? 'none' : '1px solid ' + C.chenar,
                fontSize: 14, lineHeight: 1.45, whiteSpace: 'pre-wrap',
              }}>
                {m.text || <em style={{ opacity: 0.7 }}>{t('inboxv3', 'faraText')}</em>}
                <div style={{ fontSize: 10, opacity: 0.7, marginTop: 4, textAlign: 'right' }}>
                  {dataFrumoasa(m.scris_la)}
                </div>
              </div>
            </div>
          ))}
        </div>

        {poateRaspunde && (
        <div style={{ padding: '12px 22px 18px', borderTop: '1px solid ' + C.chenar }}>
          {!inFereastra && (
            <div style={{ background: '#FFF4E5', border: '1px solid #E0A458', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: C.text, marginBottom: 8 }}>
              {t('inboxv3', 'fereastraInchisa')}
            </div>
          )}
          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            rows={2}
            disabled={!inFereastra}
            placeholder={inFereastra ? t('inboxv3', 'scrieMesajul') : t('inboxv3', 'fereastraInchisaScurt')}
            style={{
              width: '100%', boxSizing: 'border-box', border: '1px solid ' + C.chenar,
              borderRadius: 8, padding: 10, fontSize: 14, fontFamily: 'inherit', resize: 'vertical',
              background: inFereastra ? '#fff' : '#F4F5F4',
            }}
          />
          <button
            onClick={trimite}
            disabled={trimit || !text.trim() || !inFereastra}
            style={{
              marginTop: 6, border: 'none', background: C.primar, color: '#fff', borderRadius: 999,
              padding: '7px 18px', fontSize: 13, fontFamily: 'inherit',
              cursor: trimit || !text.trim() || !inFereastra ? 'default' : 'pointer',
              opacity: trimit || !text.trim() || !inFereastra ? 0.5 : 1,
            }}
          >
            {trimit ? t('inboxv3', 'seTrimite') : t('inboxv3', 'trimite')}
          </button>
        </div>
        )}
      </div>
    </div>
  );
}

// Alarma nu poate pleca nicaieri din aplicatie: singurul e-mail care iese vreodata e invitatia
// de cont, prin Supabase. Pana la Faza 7 (serviciu de e-mail + DNS pe newmind.ro), vestea te
// asteapta AICI. Ieftina, dar te anunta doar daca esti deja pe ecran — spus pe fata in plan.
function BandaDeVal({ carduri, onDeschide }) {
  const acum = Date.now();
  const proaspete = (carduri || []).filter(
    c => c.alarma_la && (acum - new Date(c.alarma_la).getTime()) < 24 * 3600 * 1000,
  );
  if (!proaspete.length) return null;

  return (
    <div style={{ marginBottom: 16 }}>
      {proaspete.map(c => (
        <div
          key={c.postare_id}
          style={{
            display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
            background: '#FFF4E5', border: '1px solid #E0A458', borderRadius: 10,
            padding: '10px 14px', marginBottom: 8,
          }}
        >
          <span style={{ fontSize: 18 }}>🔥</span>
          <span style={{ fontSize: 14, color: C.text }}>
            <strong>{t('inboxv3', 'val')}</strong>{' '}
            {t('inboxv3', 'valDescriere', {
              client: c.client_nume, varf: c.alarma_varf,
              prag: c.alarma_prag, data: dataFrumoasa(c.alarma_la),
            })}
          </span>
          <button
            onClick={() => onDeschide(c)}
            style={{
              marginLeft: 'auto', border: '1px solid ' + C.primar, background: '#fff',
              color: C.primar, borderRadius: 999, padding: '4px 14px', fontSize: 13,
              fontFamily: 'inherit', cursor: 'pointer',
            }}
          >
            {t('inboxv3', 'deschide')}
          </button>
        </div>
      ))}
    </div>
  );
}

function Cifra({ valoare, eticheta, accent = false }) {
  return (
    <div style={{ textAlign: 'center', minWidth: 62 }}>
      <div style={{ fontSize: accent ? 26 : 20, fontWeight: 600, color: accent && valoare > 0 ? C.primar : C.text, lineHeight: 1.1 }}>
        {valoare}
      </div>
      <div style={{ fontSize: 11, color: C.sters, marginTop: 2 }}>{eticheta}</div>
    </div>
  );
}

function Card({ card, onDeschide, onSterge = null, deRaspuns = 0, onDeschideCiorne = null }) {
  const nativ = card.sursa === 'nativ';
  // Cifra de pe card e cea de la Facebook, nu cate randuri avem noi: culegerea are plafon pe
  // rulare, deci ale noastre pot fi mai putine pana coboara adanc.
  const total = typeof card.comentarii_total === 'number' ? card.comentarii_total : Number(card.culese || 0);
  const noi = Number(card.noi || 0);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onDeschide}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onDeschide(); } }}
      style={{
        textAlign: 'left', cursor: 'pointer', background: '#fff',
        border: '1px solid ' + (noi > 0 ? C.primar : C.chenar),
        borderRadius: 14, padding: 16, display: 'flex', flexDirection: 'column', gap: 10,
        boxShadow: noi > 0 ? '0 1px 6px rgba(26,107,60,0.12)' : 'none', font: 'inherit',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {/* Numele clientului sta pe card, nu doar in panou: pe „Toti clientii" cardurile
            sunt amestecate si fara el nu stii al cui e. */}
        <span style={{ fontSize: 13, fontWeight: 600, color: C.inchis }}>{card.client_nume}</span>
        <span title={card.retea === 'instagram' ? 'Instagram' : 'Facebook'}
              style={{ display: 'inline-flex', alignItems: 'center' }}>
          <IconRetea id={card.retea || 'facebook'} size={15} />
        </span>
        {deRaspuns > 0 && (
          <span title={t('inboxv3', 'deRaspunsTitlu')}
                onClick={e => { if (onDeschideCiorne) { e.stopPropagation(); onDeschideCiorne(); } }}
                style={{ cursor: 'pointer', padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600, background: '#FFF4E5', color: '#9A5B00', border: '1px solid #E0A458' }}>
            ✍ {t('inboxv3', 'deRaspuns', { n: deRaspuns })}
          </span>
        )}
        {card.alarma_la && (
          <span title={t('inboxv3', 'valTitlu', { varf: card.alarma_varf || '', prag: card.alarma_prag || '' })}
                style={{ fontSize: 14 }}>🔥</span>
        )}
        <span style={{
          marginLeft: 'auto', padding: '2px 8px', borderRadius: 999, fontSize: 11,
          background: nativ ? '#F2F0E9' : C.mentol, color: nativ ? '#7A6F55' : C.primar,
        }}>
          {nativ ? t('inboxv3', 'aPostatClientul') : 'NM Social'}
        </span>
        {onSterge && (
          <button
            type="button"
            title={t('inboxv3', 'stergeCardul')}
            onClick={e => { e.stopPropagation(); onSterge(); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 15, lineHeight: 1, padding: '2px 4px', opacity: 0.45 }}
          >
            🗑
          </button>
        )}
      </div>

      <div style={{ fontSize: 12, color: C.sters, marginTop: -6 }}>{dataFrumoasa(card.publicat_la)}</div>

      <div style={{ fontSize: 14, color: C.text, lineHeight: 1.45, minHeight: 42 }}>
        {taieLaCuvant(card.text_postare, 130) || <span style={{ color: C.sters }}>{t('inboxv3', 'postareFaraText')}</span>}
      </div>

      <div style={{ display: 'flex', gap: 6, borderTop: '1px solid ' + C.chenar, paddingTop: 10, flexWrap: 'wrap' }}>
        <Cifra valoare={noi} eticheta={t('inboxv3', 'cifraNoi')} accent />
        <Cifra valoare={total} eticheta={t('inboxv3', 'cifraComentarii')} />
        {/* Reactiile primite de COMENTARII, nu de postare. Facebook nu da un total al lor, deci
            se aduna din ce am cules; pe o postare inca neculeasa pana la capat vor fi putin sub
            realitate. */}
        <Cifra valoare={Number(card.reactii_total || 0)} eticheta={t('inboxv3', 'cifraReactii')} />
        {/* „Ai raspuns" cuprinde TOT ce a primit un semn de la client — comentarii de sus si
            raspunsuri din mijlocul firelor, prin vorbe sau prin like (cerut de ea, 18 sept). */}
        <Cifra valoare={Number(card.ai_raspuns || 0)} eticheta={t('inboxv3', 'cifraAiRaspuns')} />
      </div>
    </div>
  );
}

// ============================================================
// Stratul de AI pe ecran (18 septembrie 2026). Plan: §14.
//
// Se stinge dintr-un singur loc: `inboxAiVizibil`. La un client fara drept de AI butoanele nu
// apar deloc, iar dedesubt ramane un Inbox intreg — clasament, filtre pe fapte, ascundere si
// raspuns scrise de om.
//
// Doua gramezi, nu una: teancul se aproba dintr-un buton, „la tine" niciodata. Vezi §14.8.
// ============================================================

// Filtrele sunt asezate dupa FAPTA, nu dupa cum gandeste modelul (cerut de ea, 18 septembrie:
// „ar trebui sa imi apara urmatoarele categorii: comentarii la care e bine sa dai un raspuns, la
// care nu trebuie raspuns, doar like, si ascunde"). Fiecare vine cu butonul lui, acolo unde se
// foloseste — nu toate deodata, sus.
// `eticheta` e CHEIA din `i18n`, nu textul: filtrele sunt o constanta de modul, iar textul se ia
// la randare, ca sa se schimbe si limba odata cu el.
const FILTRE = [
  { id: 'toate', eticheta: 'filtruToate' },
  { id: 'de_raspuns', eticheta: 'filtruDeRaspuns', fapta: 'raspuns' },
  { id: 'de_like', eticheta: 'filtruDoarLike', fapta: 'like' },
  { id: 'de_ascuns', eticheta: 'filtruDeAscuns', fapta: 'ascunde' },
  { id: 'la_tine', eticheta: 'filtruLaTine', fapta: 'la_tine' },
  { id: 'fara_raspuns', eticheta: 'filtruFaraRaspuns', fapta: 'nimic' },
  { id: 'de_recitit', eticheta: 'filtruDeRecitit', deRecitit: true },
  { id: 'propuneri', eticheta: 'filtruCuCiorne', propuneri: true },
  { id: 'ascunse', eticheta: 'filtruAscunse', ascunse: true },
  // „Rămase" = ce n-a fost atins deloc: fara raspuns, fara like, fara ascundere, fara raspuns al
  // clientului dedesubt. Se numea „Fara semn" si nu se intelegea de pe ecran.
  { id: 'fara_semn', eticheta: 'filtruRamase' },
];

function potrivesteFiltrul(c, f, atinsFn) {
  if (f.id === 'toate') return true;
  if (f.id === 'fara_semn') return !atinsFn(c);
  // Regula care tine tot ecranul (ceruta de ea, 18 septembrie): o categorie de LUCRU arata doar
  // ce n-a fost atins. Ai dat like, ai ascuns, ai raspuns — sau a raspuns clientul de pe telefon —
  // randul iese de aici si ramane in „Toate", cu bifa lui si cu firul intreg. Ca sa nu te uiti de
  // doua ori la acelasi comentariu.
  // Ce nu mai exista pe retea iese din TOATE listele de lucru: nu se mai poate face nimic cu el.
  if (f.id !== 'toate' && c.sters_de_autor) return false;
  if (f.fapta) return c.fapta_propusa === f.fapta && !atinsFn(c);
  if (f.categorie) return c.categorie === f.categorie;
  // Ce a primit deja un semn nu mai are de ce sa fie „recitit": te-ai uitat (spus de ea, 18 sept).
  if (f.deRecitit) return !!c.de_recitit && !atinsFn(c);
  if (f.propuneri) return !!c.propunere && !c.raspuns_trimis_la;
  if (f.ascunse) return !!c.ascuns;
  return true;
}

function Cip({ eticheta, numar, activ, onClick, culoare = null }) {
  return (
    <button
      onClick={onClick}
      style={{
        border: '1px solid ' + (activ ? (culoare || C.inchis) : C.chenar),
        background: activ ? (culoare || C.inchis) : '#fff',
        color: activ ? '#fff' : C.text,
        borderRadius: 999, padding: '4px 12px', fontSize: 12, cursor: 'pointer',
        fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 6,
      }}
    >
      {eticheta}
      <span style={{
        background: activ ? 'rgba(255,255,255,0.25)' : C.mentol,
        color: activ ? '#fff' : C.primar,
        borderRadius: 999, padding: '0 6px', fontSize: 11,
      }}>{numar}</span>
    </button>
  );
}

function BaraAI({ card, comentarii, atinsFn, onGata, inFiltru = null, numeFiltru = '', onFiltru = null, idFiltru = 'toate' }) {
  const [ruleaza, setRuleaza] = useState(null);
  const [progres, setProgres] = useState('');
  const [ultimCost, setUltimCost] = useState(null);
  const [prag, setPrag] = useState(card.prag_raspuns || 0);
  const [socoteala, setSocoteala] = useState(null);

  // Pragul nu tine de model, ci de grila aplicata in cod. Deci se poate schimba DUPA ce s-au citit
  // comentariile, iar faptele se recalculeaza pe loc si fara niciun cost. Citesti o data, apoi
  // incerci pragul cat vrei.
  async function schimbaPragul(nou) {
    setPrag(nou);
    await supabase.from('postari').update({ prag_raspuns: nou }).eq('id', card.postare_id);
    await supabase.functions.invoke('inbox-ai', { body: { postare_id: card.postare_id, mod: 'regrila' } });
    await onGata();
  }

  // Aceeasi socoteala ca in functie: ce a primit deja un semn nu se mai citeste, ca sa nu platesti
  // o eticheta pe care n-o vei folosi.
  const fara_triaj = comentarii.filter(c => !c.triaj_la && !c.al_paginii && !atinsFn(c)).length;
  const deAscuns = (inFiltru || comentarii).filter(c => c.fapta_propusa === 'ascunde' && !c.ascuns);
  const deLike = (inFiltru || comentarii).filter(c => c.fapta_propusa === 'like' && !c.like_dat && c.retea !== 'instagram');
  // Pe „Toate" se lucreaza dupa grila; intr-un filtru anume, pe ce se vede acolo — asa poti cere
  // propuneri si pentru „De recitit", unde grila n-a stiut ce sa zica.
  const dupaGrila = comentarii.filter(c =>
    c.fapta_propusa === 'raspuns' && !c.propunere && !c.raspuns_trimis_la && !atinsFn(c));
  const deRaspuns = inFiltru
    ? inFiltru.filter(c => !c.propunere && !c.raspuns_trimis_la && !c.al_paginii && !atinsFn(c))
    : dupaGrila;

  // Estimarea, din masuratori, nu din socoteala pe hartie: pe postarea lui Dan din 16 septembrie,
  // 40 de comentarii triate au costat 0,059 $, adica 0,0015 $ bucata — de cinci ori peste ce
  // calculasem. La raspunsuri iesirea e text adevarat, deci socoteala e alta.
  const banuit = (n, feluri) => {
    const usd = feluri === 'triaj' ? n * 0.0015 : n * 0.004;
    return usd < 0.01
      ? t('inboxv3', 'subUnCent')
      : t('inboxv3', 'costEstimat', { usd: usd.toFixed(2), ron: (usd * 4.6).toFixed(2) });
  };

  async function cheama(mod) {
    setRuleaza(mod);
    setProgres('');
    let costAdunat = 0;
    let total = 0;
    try {
      // Valurile mari nu incap intr-o cerere: functia intoarce `ramase` si o chemam din nou.
      for (let tura = 0; tura < 30; tura++) {
        const { data, error } = await supabase.functions.invoke('inbox-ai', {
          body: {
            postare_id: card.postare_id,
            mod,
            ...(mod === 'raspunsuri' && inFiltru ? { mesaj_ids: deRaspuns.map(c => c.id) } : {}),
          },
        });
        if (error) throw new Error(await detaliuEroare(error));
        total += data.lucrate || 0;
        costAdunat += (data.cost && data.cost.usd) || 0;
        setProgres(total + (data.ramase ? ' din ' + (total + data.ramase) : ''));
        // Oprire si cand nu mai progreseaza: daca o tura nu lucreaza nimic, urmatoarele n-ar face
        // decat sa plateasca intrarea din nou (paguba vazuta pe viu, 18 septembrie).
        if (!data.ramase || !data.lucrate) break;
      }
      setUltimCost({ usd: costAdunat, ron: costAdunat * 4.6, n: total });
      await onGata();
      // Dupa ce s-au scris, le si arata: altfel raman imprastiate in lista si trebuie cautate.
      if (mod === 'raspunsuri' && total > 0 && onFiltru) onFiltru('propuneri');
    } catch (err) {
      // „non-2xx status code" fara alt detaliu inseamna, de obicei, ca functia a depasit timpul.
      // Ce s-a lucrat pana atunci e salvat, deci reluarea continua de unde a ramas.
      alert(String(err.message || '').includes('non-2xx')
        ? t('inboxv3', 'cerereaPreaLunga')
        : t('inboxv3', 'nuSAPutut', { eroare: err.message }));
    } finally {
      setRuleaza(null);
      setProgres('');
    }
  }

  async function porneste(mod, n, fel) {
    if (!n) return;
    const ok = window.confirm(
      t('inboxv3', mod === 'triaj' ? 'intrebareTriaj' : 'intrebareRaspunsuri', { n })
      + t('inboxv3', 'intrebareCost', { cost: banuit(n, fel) })
    );
    if (ok) cheama(mod);
  }

  // Faptele in bloc merg ca o COADA, nu ca o rafala: 363 de ascunderi inseamna ~700 de cereri cu
  // tot cu verificari, iar o rafala ar arde plafonul de care depinde publicarea programata.
  async function inBloc(lista, cum) {
    if (!lista.length) return;
    const ce = t('inboxv3', cum === 'ascunde' ? 'blocAscund' : 'blocLike');
    if (!window.confirm(t('inboxv3', 'intrebareBloc', { ce, n: lista.length }))) return;

    setRuleaza(cum);
    let reusite = 0;
    for (let i = 0; i < lista.length; i++) {
      setProgres((i + 1) + ' din ' + lista.length);
      const c = lista[i];
      try {
        if (cum === 'ascunde') {
          const corp = c.retea === 'instagram'
            ? { mesaj_id: c.id, ascunde: true }
            : { mesaj_id: c.id, actiune: 'ascunde' };
          const fn = c.retea === 'instagram' ? 'instagram-reply' : 'facebook-moderate';
          const { error } = await supabase.functions.invoke(fn, { body: corp });
          if (!error) reusite++;
        } else {
          const { error } = await supabase.functions.invoke('facebook-like', {
            body: { mesaj_id: c.id, like: true },
          });
          if (!error) reusite++;
        }
      } catch (e) { /* se numara doar reusitele; socoteala vine la final */ }
      await new Promise(r => setTimeout(r, 900));
    }
    setRuleaza(null);
    setProgres('');
    await onGata();
    // Socoteala ramane pe ecran. O fereastra cu „OK" dispare la prima apasare si pe urma nu
    // mai ai unde sa te uiti (intrebarea ei, 18 septembrie: „unde le vad?").
    setSocoteala({ cum, reusite, din: lista.length });
  }

  const ocupat = !!ruleaza;

  return (
    <div style={{ background: '#F7F9F8', border: '1px solid ' + C.chenar, borderRadius: 10, padding: 12, marginBottom: 14 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {fara_triaj > 0 && (
          <button
            onClick={() => porneste('triaj', fara_triaj, 'triaj')}
            disabled={ocupat}
            style={{
              border: 'none', background: C.inchis, color: '#fff', borderRadius: 999,
              padding: '6px 16px', fontSize: 13, fontFamily: 'inherit',
              cursor: ocupat ? 'default' : 'pointer', opacity: ocupat ? 0.5 : 1,
            }}
          >
            {ruleaza === 'triaj'
              ? t('inboxv3', 'ruleazaTriaj', { progres })
              : t('inboxv3', 'butonTriaj', { n: fara_triaj })}
          </button>
        )}

        {deRaspuns.length > 0 && ['toate', 'de_raspuns', 'de_recitit'].includes(idFiltru) && (
          <button
            onClick={() => porneste('raspunsuri', deRaspuns.length, 'raspuns')}
            disabled={ocupat}
            style={{
              border: '1px solid ' + C.primar, background: '#fff', color: C.primar, borderRadius: 999,
              padding: '6px 16px', fontSize: 13, fontFamily: 'inherit',
              cursor: ocupat ? 'default' : 'pointer', opacity: ocupat ? 0.5 : 1,
            }}
          >
            {ruleaza === 'raspunsuri'
              ? t('inboxv3', 'ruleazaRaspunsuri', { progres })
              : (inFiltru
                ? t('inboxv3', 'butonRaspunsuriFiltru', { filtru: numeFiltru, n: deRaspuns.length })
                : t('inboxv3', 'butonRaspunsuri', { n: deRaspuns.length }))}
          </button>
        )}

        {deAscuns.length > 0 && ['toate', 'de_ascuns'].includes(idFiltru) && (
          <button
            onClick={() => inBloc(deAscuns, 'ascunde')}
            disabled={ocupat}
            style={{
              border: '1px solid #E6C3C0', background: '#fff', color: C.alarma, borderRadius: 999,
              padding: '6px 16px', fontSize: 13, fontFamily: 'inherit',
              cursor: ocupat ? 'default' : 'pointer', opacity: ocupat ? 0.5 : 1,
            }}
          >
            {ruleaza === 'ascunde'
              ? t('inboxv3', 'ruleazaAscunde', { progres })
              : t('inboxv3', 'butonAscundeTeancul', { n: deAscuns.length })}
          </button>
        )}

        {deLike.length > 0 && ['toate', 'de_like'].includes(idFiltru) && (
          <button
            onClick={() => inBloc(deLike, 'like')}
            disabled={ocupat}
            style={{
              border: '1px solid ' + C.chenar, background: '#fff', color: C.text, borderRadius: 999,
              padding: '6px 16px', fontSize: 13, fontFamily: 'inherit',
              cursor: ocupat ? 'default' : 'pointer', opacity: ocupat ? 0.5 : 1,
            }}
          >
            {ruleaza === 'like'
              ? t('inboxv3', 'ruleazaLike', { progres })
              : t('inboxv3', 'butonLikeToate', { n: deLike.length })}
          </button>
        )}

        {comentarii.some(c => c.triaj_la) && (
          <label style={{ fontSize: 12, color: C.sters, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {t('inboxv3', 'pragEticheta')}
            <select
              value={prag}
              onChange={e => schimbaPragul(Number(e.target.value))}
              disabled={ocupat}
              style={{
                border: '1px solid ' + C.chenar, borderRadius: 8, padding: '3px 6px',
                fontSize: 12, fontFamily: 'inherit', background: '#fff',
              }}
            >
              <option value={0}>{t('inboxv3', 'pragToate')}</option>
              {[2, 5, 20, 50].map(n => (
                <option key={n} value={n}>{t('inboxv3', 'pragReactii', { n })}</option>
              ))}
            </select>
          </label>
        )}

        {ultimCost && (
          <span style={{ fontSize: 12, color: C.sters, marginLeft: 'auto' }}>
            {t('inboxv3', 'lucrate', { n: ultimCost.n, usd: ultimCost.usd.toFixed(3), ron: ultimCost.ron.toFixed(2) })}
          </span>
        )}
      </div>

      {socoteala && (
        <div style={{
          marginTop: 10, padding: '8px 12px', borderRadius: 8, fontSize: 13,
          background: socoteala.reusite === socoteala.din ? C.mentol : '#FFF4E5',
          border: '1px solid ' + (socoteala.reusite === socoteala.din ? C.primar : '#E0A458'),
          color: C.text, display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <span>
            {t('inboxv3', socoteala.cum === 'ascunde' ? 'socotealaAscunse' : 'socotealaLike')}{' '}
            <strong>{t('inboxv3', 'socotealaDin', { reusite: socoteala.reusite, din: socoteala.din })}</strong>
            {t('inboxv3', socoteala.reusite < socoteala.din ? 'socotealaPartial' : 'socotealaTot')}
          </span>
          <button
            onClick={() => setSocoteala(null)}
            style={{ marginLeft: 'auto', border: 'none', background: 'transparent', color: C.sters, cursor: 'pointer', fontSize: 16 }}
          >×</button>
        </div>
      )}

      {comentarii.some(c => c.fapta_propusa === 'la_tine') && (
        <div style={{ fontSize: 12, color: C.sters, marginTop: 8 }}>
          {t('inboxv3', 'ostileSiVizibile1', { n: comentarii.filter(c => c.fapta_propusa === 'la_tine').length })}
          <strong>{t('inboxv3', 'ostileSiVizibile2')}</strong>
          {t('inboxv3', 'ostileSiVizibile3')}
        </div>
      )}
    </div>
  );
}

// ============================================================
// Botul de comentarii pe ecran (etapa 4, bucata 1 — 12 octombrie 2026).
//
// Un rand cu cifrele scenariului postarii si, pentru super admin, butonul care opreste botul pe
// toata pagina. Scenariul se scrie deocamdata direct in baza; formularul vine in bucata 2.
// Cifrele ies din `bot_trimiteri`, jurnalul functiei `bot-comentarii`: un rand pentru fiecare
// comentariu la care botul a hotarat ceva.
//
// Procentul de apasare e singura masura a mesajelor care ajung la om: Facebook raspunde
// „trimis" si cand mesajul aterizeaza la Spam (vazut pe viu, 12 octombrie).
// Lista „cine a apasat ce" e o evidenta de oameni si raspunsuri: o vede doar super adminul,
// pana se hotarasc regulile ei (etapa 5).
// ============================================================
const MOTIVE_BOT = ['conversatie_deschisa', 'deja_in_30_de_zile', 'deja_la_postare', 'ascuns', 'sters',
  'fara_autor', 'autor_pagina', 'pagina_insasi', 'privat_trimis_de_om', 'filtru_fara_dm', 'filtru_necitit'];

// Formularul scenariului (etapa 4, bucata 2). Regulile ei, 12 octombrie 2026:
//   - raspunsurile la butoane se pot schimba oricand;
//   - intrebarea si butoanele (cate sunt, ce scrie pe ele) doar cu scenariul OPRIT;
//   - cand ele se schimba, scenariul trece la o varianta noua, iar cea veche se pastreaza in
//     `istoric`: mesajele deja plecate raman cu butoanele lor, iar cine apasa pe ele primeste
//     raspunsul variantei LUI, nu al celei noi. Cifrele se tin pe varianta.
// Limitele sunt ale Messenger: intrebarea cel mult 640 de caractere, cel mult 3 butoane in
// bula, titlul unui buton cel mult 20 de caractere.
// De la cate mesaje trimise in sus are rost un procent de apasare.
const BOT_PRAG_PROCENT = 20;
const BOT_MAX_INTREBARE = 640;
const BOT_MAX_TITLU = 20;
const BOT_MAX_RASPUNS = 1000;

function FormularBot({ card, scenariu, onGata, onRenunta, titluAbonare = '', pozaAbonare = '', butonAbonare = '', areLinkConf = false, linkConf = '', textConfirmare = '' }) {
  const pornit = !!(scenariu && scenariu.pornit);
  // Dupa raspuns, botul trimite si cartonasul de abonare al paginii (etapa 6). Se poate
  // schimba oricand, si cu botul pornit: nu atinge intrebarea, deci nu face varianta noua.
  const [cuAbonare, setCuAbonare] = useState(!!(scenariu && scenariu.cu_abonare));
  // Tema scenariului (15 oct 2026): dupa ea se aleg abonatii la mesaje („toti de pe tema cancer”).
  const [tema, setTema] = useState(scenariu && scenariu.tema ? scenariu.tema : '');
  const [teme, setTeme] = useState([]);
  useEffect(() => {
    supabase.from('bot_scenarii').select('tema').eq('client_id', card.client_id).not('tema', 'is', null)
      .then(({ data }) => setTeme([...new Set((data || []).map(x => x.tema))].sort()));
  }, [card.client_id]);
  // Intrebarea de opinie (etapa 5, 14 octombrie 2026): se numara apasarile, fara nume. Se
  // alege inainte de prima pornire; dupa, baza n-o mai lasa schimbata (`bot_opinie.sql`).
  const [doarNumere, setDoarNumere] = useState(!!(scenariu && scenariu.doar_numere));
  const opinieFixa = !!(scenariu && scenariu.pornit_la);
  const [intrebare, setIntrebare] = useState(scenariu ? (scenariu.intrebare || '') : '');
  const [butoane, setButoane] = useState(() => {
    const b = scenariu && Array.isArray(scenariu.butoane) ? scenariu.butoane : [];
    return b.length
      ? b.map(x => ({ cod: x.cod, titlu: x.titlu || '', raspuns: x.raspuns || '' }))
      : [{ cod: 'b1', titlu: 'DA', raspuns: '' }, { cod: 'b2', titlu: 'NU', raspuns: '' }];
  });
  const [ocupat, setOcupat] = useState(false);

  function schimba(i, camp, valoare) {
    setButoane(prev => prev.map((b, j) => (j === i ? { ...b, [camp]: valoare } : b)));
  }
  function adauga() {
    // Codul e al butonului pe viata: dupa el se recunoaste apasarea. Nu se refoloseste unul scos.
    setButoane(prev => [...prev, { cod: 'b' + Date.now().toString(36), titlu: '', raspuns: '' }]);
  }
  function scoate(i) {
    setButoane(prev => prev.filter((_, j) => j !== i));
  }

  const curate = butoane.map(b => ({ cod: b.cod, titlu: b.titlu.trim(), raspuns: b.raspuns.trim() }));
  const titluri = curate.map(b => b.titlu.toLowerCase());
  const problema =
    !intrebare.trim() ? 'botLipsaIntrebare'
    : intrebare.trim().length > BOT_MAX_INTREBARE ? 'botIntrebareLunga'
    : !curate.length ? 'botLipsaButoane'
    : curate.some(b => !b.titlu) ? 'botLipsaTitlu'
    : curate.some(b => b.titlu.length > BOT_MAX_TITLU) ? 'botTitluLung'
    : new Set(titluri).size !== titluri.length ? 'botTitluriLaFel'
    : curate.some(b => b.raspuns.length > BOT_MAX_RASPUNS) ? 'botRaspunsLung'
    : null;

  async function salveaza() {
    if (ocupat || problema) return;
    setOcupat(true);
    const acum = new Date().toISOString();
    let error = null;
    if (!scenariu) {
      ({ error } = await supabase.from('bot_scenarii').insert({
        client_id: card.client_id, postare_id: card.postare_id, pornit: false,
        intrebare: intrebare.trim(), butoane: curate, cu_abonare: cuAbonare, doar_numere: doarNumere,
        tema: tema.trim().toLowerCase() || null,
      }));
    } else {
      const vechi = Array.isArray(scenariu.butoane) ? scenariu.butoane : [];
      const miezSchimbat = intrebare.trim() !== scenariu.intrebare
        || vechi.length !== curate.length
        || curate.some((b, i) => b.cod !== vechi[i].cod || b.titlu !== vechi[i].titlu);
      const corp = { butoane: curate, cu_abonare: cuAbonare, actualizat_la: acum, tema: tema.trim().toLowerCase() || null };
      if (!opinieFixa) corp.doar_numere = doarNumere;
      if (miezSchimbat) {
        corp.intrebare = intrebare.trim();
        corp.versiune = (scenariu.versiune || 1) + 1;
        corp.istoric = [
          ...(Array.isArray(scenariu.istoric) ? scenariu.istoric : []),
          { versiune: scenariu.versiune || 1, intrebare: scenariu.intrebare, butoane: vechi, pana_la: acum },
        ];
      }
      ({ error } = await supabase.from('bot_scenarii').update(corp).eq('id', scenariu.id));
    }
    setOcupat(false);
    if (error) { alert(t('inboxv3', 'nuSAPutut', { eroare: error.message })); return; }
    onGata();
  }

  const camp = { width: '100%', boxSizing: 'border-box', border: '1px solid ' + C.chenar, borderRadius: 8, padding: '8px 10px', fontSize: 14, fontFamily: 'inherit', color: C.text };
  const eticheta = { fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 4 };
  const numar = (n, max) => (
    <span style={{ fontSize: 12, color: n > max ? C.alarma : C.sters }}>{n}/{max}</span>
  );

  return (
    <div style={{ border: '1px solid ' + C.chenar, borderLeft: '4px solid ' + C.primar, borderRadius: 10, padding: 14, marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ fontWeight: 600, color: C.inchis, fontSize: 15 }}>
        {scenariu ? t('inboxv3', 'botSchimbaTitlu') : t('inboxv3', 'botPune')}
      </div>
      {/* Botul scrie doar la postarile din ultimele zile (15 oct 2026), acelasi prag ca filtrul. */}
      <div style={{ fontSize: 13, color: C.sters }}>{t('inboxv3', 'botDoarZile')}</div>

      {pornit && (
        <div style={{ background: C.mentol, borderRadius: 8, padding: '8px 10px', fontSize: 13, color: C.text }}>
          {t('inboxv3', 'botPornitDoarRaspunsuri')}
        </div>
      )}

      <div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <div style={eticheta}>{t('inboxv3', 'botCampIntrebare')}</div>
          <span style={{ marginLeft: 'auto' }}>{numar(intrebare.trim().length, BOT_MAX_INTREBARE)}</span>
        </div>
        <textarea
          value={intrebare}
          onChange={e => setIntrebare(e.target.value)}
          disabled={pornit}
          rows={3}
          placeholder={t('inboxv3', 'botIntrebareExemplu')}
          style={{ ...camp, resize: 'vertical', background: pornit ? '#F4F6F5' : '#fff' }}
        />
      </div>

      {butoane.map((b, i) => (
        <div key={b.cod} style={{ border: '1px solid ' + C.chenar, borderRadius: 8, padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ ...eticheta, marginBottom: 0 }}>{t('inboxv3', 'botCampButon', { n: i + 1 })}</div>
            <span>{numar(b.titlu.trim().length, BOT_MAX_TITLU)}</span>
            {!pornit && butoane.length > 1 && (
              <button type="button" onClick={() => scoate(i)}
                style={{ marginLeft: 'auto', border: 'none', background: 'transparent', color: C.alarma, cursor: 'pointer', fontSize: 13 }}>
                {t('inboxv3', 'botScoateButon')}
              </button>
            )}
          </div>
          <input
            value={b.titlu}
            onChange={e => schimba(i, 'titlu', e.target.value)}
            disabled={pornit}
            placeholder={t('inboxv3', 'botTitluExemplu')}
            style={{ ...camp, background: pornit ? '#F4F6F5' : '#fff' }}
          />
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <div style={{ fontSize: 13, color: C.text }}>{t('inboxv3', 'botCampRaspuns')}</div>
            <span style={{ marginLeft: 'auto' }}>{numar(b.raspuns.trim().length, BOT_MAX_RASPUNS)}</span>
          </div>
          <textarea
            value={b.raspuns}
            onChange={e => schimba(i, 'raspuns', e.target.value)}
            rows={2}
            placeholder={t('inboxv3', 'botRaspunsExemplu')}
            style={{ ...camp, resize: 'vertical' }}
          />
        </div>
      ))}

      <div>
        <div style={eticheta}>{t('inboxv3', 'botCampTema')}</div>
        <input value={tema} onChange={e => setTema(e.target.value.slice(0, 40))} list={'teme-' + card.client_id}
          placeholder={t('inboxv3', 'botTemaExemplu')} style={{ ...camp, maxWidth: 320 }} />
        <datalist id={'teme-' + card.client_id}>{teme.map(x => <option key={x} value={x} />)}</datalist>
        <div style={{ fontSize: 13, color: C.sters, marginTop: 4 }}>{t('inboxv3', 'botTemaExplicatie')}</div>
      </div>

      {!pornit && butoane.length < 3 && (
        <div>
          <button type="button" onClick={adauga}
            style={{ border: '1px dashed ' + C.primar, color: C.primar, background: '#fff', borderRadius: 8, padding: '6px 12px', fontSize: 13, cursor: 'pointer' }}>
            {t('inboxv3', 'botAdaugaButon')}
          </button>
        </div>
      )}

      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 14, color: C.text, cursor: opinieFixa ? 'default' : 'pointer' }}>
        <input type="checkbox" checked={doarNumere} disabled={opinieFixa} onChange={e => setDoarNumere(e.target.checked)} style={{ marginTop: 3 }} />
        <span>
          {t('inboxv3', 'botDoarNumere')}
          <span style={{ display: 'block', fontSize: 13, color: C.sters }}>
            {t('inboxv3', opinieFixa ? 'botDoarNumereFix' : 'botDoarNumereExplicatie')}
          </span>
        </span>
      </label>

      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 14, color: C.text, cursor: 'pointer' }}>
        <input type="checkbox" checked={cuAbonare} onChange={e => setCuAbonare(e.target.checked)} style={{ marginTop: 3 }} />
        <span>
          {t('inboxv3', 'botCuAbonare')}
          <span style={{ display: 'block', fontSize: 13, color: titluAbonare ? C.sters : C.alarma }}>
            {titluAbonare ? t('inboxv3', 'botCuAbonareTitlu', { titlu: titluAbonare }) : t('inboxv3', 'botCuAbonareFaraTitlu')}
          </span>
        </span>
      </label>

      {/* Cartonasul pleaca imediat dupa raspuns. Fara o vorba care sa duca spre el, cade din senin,
          cu fraza Meta despre reduceri cu tot (observatia ei, 14 octombrie 2026, comparand cu alte
          pagini: la ele raspunsul se incheie cu „apasati butonul de mai jos"). */}
      {cuAbonare && (
        <div style={{ background: C.mentol, borderRadius: 8, padding: '8px 10px', fontSize: 13, color: C.text }}>
          {t('inboxv3', 'botCuAbonareSfat')}
        </div>
      )}
      {cuAbonare && titluAbonare && !areLinkConf && (
        <div style={{ fontSize: 13, color: C.alarma }}>{t('inboxv3', 'abonatiFaraLink')}</div>
      )}
      {cuAbonare && titluAbonare && (
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 4 }}>{t('inboxv3', 'botCartonasCumArata')}</div>
          <CartonasAbonare titlu={titluAbonare} pozaUrl={pozaAbonare} pagina={card.client_nume} buton={butonAbonare} />
          {/* Si ce primeste dupa ce apasa cartonasul: confirmarea cu linkul paginii (etapa 5).
              Ceruta de ea pe 14 octombrie seara: in formular nu se vedea unde sta linkul. */}
          {areLinkConf && (
            <>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.text, margin: '12px 0 4px' }}>{t('inboxv3', 'botConfirmareCumArata')}</div>
              <div style={{ background: '#F0F2F5', borderRadius: 18, padding: 12, maxWidth: 300, fontSize: 15, color: '#050505' }}>
                <div style={{ whiteSpace: 'pre-wrap' }}>{textConfirmare || t('inboxv3', 'abonatiConfirmareDinOficiu')}</div>
                <div style={{ marginTop: 12, wordBreak: 'break-all' }}>
                  {t('inboxv3', 'abonatiPoliticaConf')}: <span style={{ color: '#0866FF', textDecoration: 'underline' }}>{linkConf}</span>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Cum vede omul mesajul in Messenger: intrebarea si butoanele in aceeasi bula. */}
      <div>
        <div style={eticheta}>{t('inboxv3', 'botPrevizualizare')}</div>
        <div style={{ background: '#F0F2F5', borderRadius: 18, padding: 12, maxWidth: 300, fontSize: 15, color: '#050505' }}>
          <div style={{ whiteSpace: 'pre-wrap', marginBottom: 10 }}>{intrebare.trim() || '…'}</div>
          {curate.map(b => (
            <div key={b.cod} style={{ background: '#fff', borderRadius: 10, padding: '8px 10px', textAlign: 'center', fontWeight: 600, marginTop: 6 }}>
              {b.titlu || '…'}
            </div>
          ))}
        </div>
      </div>

      {problema && <div style={{ color: C.alarma, fontSize: 13 }}>{t('inboxv3', problema)}</div>}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <button type="button" onClick={salveaza} disabled={ocupat || !!problema}
          style={{ border: 'none', background: C.primar, color: '#fff', borderRadius: 8, padding: '9px 16px', fontSize: 14, fontWeight: 600, cursor: (ocupat || problema) ? 'default' : 'pointer', opacity: (ocupat || problema) ? 0.5 : 1 }}>
          {t('inboxv3', 'botSalveaza')}
        </button>
        <button type="button" onClick={onRenunta}
          style={{ border: 'none', background: 'transparent', color: C.text, fontSize: 14, cursor: 'pointer' }}>
          {t('inboxv3', 'botRenunta')}
        </button>
        {!scenariu && <span style={{ fontSize: 13, color: C.sters }}>{t('inboxv3', 'botSalvatNuPornit')}</span>}
      </div>
    </div>
  );
}

function BandaBot({ card, comentarii, rol, poateBot = false }) {
  const [titluAbonare, setTitluAbonare] = useState('');
  const [pozaAbonare, setPozaAbonare] = useState('');
  const [butonAbonare, setButonAbonare] = useState('');
  const [areLinkConf, setAreLinkConf] = useState(false);
  const [linkConf, setLinkConf] = useState('');
  const [textConfirmare, setTextConfirmare] = useState('');
  const [abonati, setAbonati] = useState(0);
  const [scenariu, setScenariu] = useState(null);
  const [trimiteri, setTrimiteri] = useState([]);
  const [voturi, setVoturi] = useState([]);
  const [oprit, setOprit] = useState(false);
  const [deschis, setDeschis] = useState(false);
  const [ocupat, setOcupat] = useState(false);
  const [formular, setFormular] = useState(false);
  const eSuperAdmin = rol === 'super_admin';
  // Cine pune, schimba, porneste si opreste botul la o postare: super adminul si contul de
  // client cu bifa „Bot la comentarii" (`poateBot`). Oprirea pe TOATA pagina si lista „cine a
  // apasat ce" raman doar la super admin.
  const poateGestiona = eSuperAdmin || poateBot;

  // Pornirea si oprirea scenariului acestei postari. La fiecare pornire `pornit_la` se muta pe
  // clipa de acum: botul scrie doar celor care comenteaza DUPA pornire (regula 9), deci si
  // comentariile dintr-o pauza raman fara mesaj.
  async function comutaScenariul() {
    if (ocupat || !scenariu) return;
    const porneste = !scenariu.pornit;
    if (porneste && !window.confirm(t('inboxv3', 'botConfirmPornire'))) return;
    setOcupat(true);
    const acum = new Date().toISOString();
    const { error } = await supabase.from('bot_scenarii')
      .update(porneste ? { pornit: true, pornit_la: acum, actualizat_la: acum } : { pornit: false, actualizat_la: acum })
      .eq('id', scenariu.id);
    setOcupat(false);
    if (error) { alert(t('inboxv3', 'nuSAPutut', { eroare: error.message })); return; }
    await incarca();
  }

  const incarca = useCallback(async () => {
    const { data: sc } = await supabase
      .from('bot_scenarii')
      .select('id, pornit, pornit_la, intrebare, butoane, versiune, istoric, cu_abonare, doar_numere, tema')
      .eq('postare_id', card.postare_id)
      .maybeSingle();
    setScenariu(sc || null);
    // Setarile paginii se citesc si fara scenariu: formularul unui scenariu nou are nevoie de
    // titlul subiectului, ca sa spuna daca abonarea are sau nu cu ce porni.
    const { data: st } = await supabase.from('bot_setari').select('oprit, abonare_titlu, abonare_poza_url, abonare_buton, link_confidentialitate, text_confirmare').eq('client_id', card.client_id).maybeSingle();
    setOprit(!!(st && st.oprit));
    setTitluAbonare(st && st.abonare_titlu ? st.abonare_titlu : '');
    setPozaAbonare(st && st.abonare_poza_url ? st.abonare_poza_url : '');
    setButonAbonare(st && st.abonare_buton ? st.abonare_buton : '');
    setAreLinkConf(!!(st && st.link_confidentialitate));
    setLinkConf(st && st.link_confidentialitate ? st.link_confidentialitate : '');
    setTextConfirmare(st && st.text_confirmare ? st.text_confirmare : '');
    if (!sc) return;
    const [{ data: tr }, { count: abonatiAici }, { data: vt }] = await Promise.all([
      supabase.from('bot_trimiteri')
        .select('id, mesaj_id, stare, motiv, apasat_cod, apasat_la, versiune, cartonas_trimis_la')
        .eq('scenariu_id', sc.id)
        .limit(5000),
      supabase.from('bot_abonati').select('id', { count: 'exact', head: true })
        .eq('scenariu_id', sc.id).eq('stare', 'abonat'),
      // La intrebarea de opinie, cine ce a apasat nu exista: numerele stau aici, fara nume.
      sc.doar_numere
        ? supabase.from('bot_voturi').select('versiune, cod, numar').eq('scenariu_id', sc.id)
        : Promise.resolve({ data: [] }),
    ]);
    setTrimiteri(tr || []);
    setVoturi(vt || []);
    setAbonati(abonatiAici || 0);
  }, [card.postare_id, card.client_id]);

  useEffect(() => { incarca(); }, [incarca]);

  if (formular) {
    return (
      <FormularBot
        card={card}
        scenariu={scenariu}
        titluAbonare={titluAbonare}
        pozaAbonare={pozaAbonare}
        butonAbonare={butonAbonare}
        areLinkConf={areLinkConf}
        linkConf={linkConf}
        textConfirmare={textConfirmare}
        onRenunta={() => setFormular(false)}
        onGata={async () => { setFormular(false); await incarca(); setDeschis(true); }}
      />
    );
  }

  // Fara scenariu: super adminul vede de unde se pune unul; restul nu vad nimic.
  if (!scenariu) {
    if (!poateGestiona) return null;
    return (
      <div style={{ marginBottom: 16 }}>
        <button
          type="button"
          onClick={() => setFormular(true)}
          style={{ border: '1px dashed ' + C.primar, color: C.primar, background: '#fff', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
        >
          {t('inboxv3', 'botPune')}
        </button>
      </div>
    );
  }

  const butoane = Array.isArray(scenariu.butoane) ? scenariu.butoane : [];
  // Cifrele se tin pe varianta de ACUM a intrebarii: daca intrebarea s-a schimbat, un DA de
  // dinainte si unul de dupa nu inseamna acelasi lucru. Variantele vechi se arata separat.
  const versiune = scenariu.versiune || 1;
  const aleVariantei = trimiteri.filter(x => (x.versiune || 1) === versiune);
  const vechi = trimiteri.filter(x => (x.versiune || 1) !== versiune);
  const trimise = aleVariantei.filter(x => x.stare === 'trimis');
  const sarite = aleVariantei.filter(x => x.stare === 'sarit');
  const esuate = aleVariantei.filter(x => x.stare === 'esuat');
  const apasate = trimise.filter(x => x.apasat_la);
  const peButon = (cod) => scenariu.doar_numere
    ? voturi.filter(v => (v.versiune || 1) === versiune && v.cod === cod).reduce((s, v) => s + (v.numar || 0), 0)
    : apasate.filter(x => x.apasat_cod === cod).length;
  const procent = trimise.length ? Math.round((apasate.length / trimise.length) * 100) : null;
  // Comentariile la care botul are ce spune: de sus, ale oamenilor, de dupa pornire.
  const dupaPornire = comentarii.filter(c =>
    !c.parinte_id && !c.al_paginii && scenariu.pornit_la && c.scris_la && c.scris_la > scenariu.pornit_la);
  const hotarate = new Set(trimiteri.map(x => x.mesaj_id));
  const inAsteptare = scenariu.pornit ? dupaPornire.filter(c => !hotarate.has(c.id)).length : 0;
  const numeDupaMesaj = {};
  comentarii.forEach(c => { numeDupaMesaj[c.id] = c.autor; });

  async function comutaOprirea() {
    if (ocupat) return;
    if (!oprit && !window.confirm(t('inboxv3', 'botConfirmOprire', { client: card.client_nume }))) return;
    setOcupat(true);
    const { error } = await supabase.from('bot_setari').upsert(
      { client_id: card.client_id, oprit: !oprit, actualizat_la: new Date().toISOString() },
      { onConflict: 'client_id' },
    );
    setOcupat(false);
    if (error) { alert(t('inboxv3', 'nuSAPutut', { eroare: error.message })); return; }
    setOprit(!oprit);
  }

  const viu = scenariu.pornit && !oprit;
  const culoare = viu ? C.primar : C.alarma;
  // Un numar cu vorba lui, la singular sau la plural. In romana, de la 20 in sus se spune
  // „20 de mesaje" (dar „101 mesaje", „119 mesaje": ultimele doua cifre intre 01 si 19 nu cer „de").
  const cifra = (n, cheie) => {
    const coada = n % 100;
    const de = getLang() === 'ro' && n >= 20 && (coada === 0 || coada >= 20) ? ' de' : '';
    return <span><strong style={{ color: C.text }}>{n}</strong>{de} {t('inboxv3', n === 1 ? cheie + '1' : cheie)}</span>;
  };

  return (
    <div style={{ border: '1px solid ' + C.chenar, borderLeft: '4px solid ' + culoare, borderRadius: 10, padding: '12px 14px', marginBottom: 16 }}>
      <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', fontSize: 13, color: C.text }}>
        <strong style={{ color: culoare }}>
          {oprit ? t('inboxv3', 'botOpritPePagina') : (scenariu.pornit ? t('inboxv3', 'botPornit') : t('inboxv3', 'botOprit'))}
        </strong>
        {cifra(dupaPornire.length, 'botComentarii')}
        {cifra(trimise.length, 'botTrimise')}
        <span>
          <strong>{apasate.length}</strong> {t('inboxv3', apasate.length === 1 ? 'botAuApasat1' : 'botAuApasat')}
          {apasate.length > 0 && (
            <> ({butoane.map(b => b.titlu + ' ' + peButon(b.cod)).join(' · ')})</>
          )}
        </span>
        {cifra(sarite.length, 'botSarite')}
        {esuate.length > 0 && <span style={{ color: C.alarma }}><strong>{esuate.length}</strong> {t('inboxv3', 'botEsuate')}</span>}
        <button
          type="button"
          onClick={() => setDeschis(v => !v)}
          style={{ marginLeft: 'auto', border: 'none', background: 'transparent', color: C.primar, cursor: 'pointer', fontSize: 13, padding: 0 }}
        >
          {deschis ? t('inboxv3', 'botAscundeDetalii') : t('inboxv3', 'botDetalii')}
        </button>
      </div>

      <div style={{ fontSize: 13, color: C.text, marginTop: 6 }}>
        {/* Procentul tace la numere mici: „100%" dintr-un singur mesaj nu masoara nimic. */}
        {!trimise.length
          ? t('inboxv3', 'botFaraTrimise')
          : trimise.length >= BOT_PRAG_PROCENT
            ? t('inboxv3', 'botProcent', { p: procent })
            : t('inboxv3', 'botApasatDin', { a: apasate.length, t: trimise.length })}
        {inAsteptare > 0 && <> · {t('inboxv3', 'botInAsteptare', { n: inAsteptare })}</>}
      </div>

      {deschis && (
        <div style={{ marginTop: 12, borderTop: '1px solid ' + C.chenar, paddingTop: 12, fontSize: 13, color: C.text, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>{t('inboxv3', 'botIntrebarea')}</div>
            {scenariu.doar_numere && (
              <div style={{ color: C.sters, marginBottom: 4 }}>{t('inboxv3', 'botDoarNumereEticheta')}</div>
            )}
            {scenariu.tema && <div style={{ color: C.sters, marginBottom: 4 }}>{t('inboxv3', 'botTemaEticheta', { tema: scenariu.tema })}</div>}
            <div>{scenariu.intrebare}</div>
            {butoane.map(b => (
              <div key={b.cod} style={{ marginTop: 4 }}><strong>{b.titlu}</strong> → {b.raspuns}</div>
            ))}
          </div>

          {(sarite.length > 0 || esuate.length > 0) && (
            <div>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>{t('inboxv3', 'botDeCeSarite')}</div>
              {MOTIVE_BOT.map(m => {
                const n = sarite.filter(x => x.motiv === m).length;
                return n ? <div key={m}>{n} · {t('inboxv3', 'botMotiv_' + m)}</div> : null;
              })}
              {esuate.map(x => (
                <div key={x.id} style={{ color: C.alarma }}>{t('inboxv3', 'botEsuate')}: {x.motiv}</div>
              ))}
            </div>
          )}

          {(eSuperAdmin || rol === 'client') && !scenariu.doar_numere && apasate.length > 0 && (
            <div>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>{t('inboxv3', 'botCineCe')}</div>
              {apasate.map(x => {
                const b = butoane.find(y => y.cod === x.apasat_cod);
                return (
                  <div key={x.id}>
                    {numeDupaMesaj[x.mesaj_id] || t('inboxv3', 'botOmNecunoscut')} — <strong>{b ? b.titlu : x.apasat_cod}</strong>
                    <span style={{ color: C.sters }}> · {dataFrumoasa(x.apasat_la)}</span>
                  </div>
                );
              })}
            </div>
          )}

          {scenariu.cu_abonare && (
            <div>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>{t('inboxv3', 'botAbonareTitlu')}</div>
              <div>
                {t('inboxv3', 'botAbonareCifre', { cartonase: trimiteri.filter(x => x.cartonas_trimis_la).length, abonati })}
              </div>
              {!titluAbonare && <div style={{ color: C.alarma }}>{t('inboxv3', 'botCuAbonareFaraTitlu')}</div>}
              {titluAbonare && !areLinkConf && <div style={{ color: C.alarma }}>{t('inboxv3', 'abonatiFaraLink')}</div>}
              {titluAbonare && (
                <div style={{ marginTop: 8 }}>
                  <CartonasAbonare titlu={titluAbonare} pozaUrl={pozaAbonare} pagina={card.client_nume} buton={butonAbonare} />
                </div>
              )}
            </div>
          )}

          {vechi.length > 0 && (
            <div style={{ color: C.sters }}>
              {t('inboxv3', 'botVarianteVechi', {
                trimise: vechi.filter(x => x.stare === 'trimis').length,
                apasate: vechi.filter(x => x.apasat_la).length,
              })}
            </div>
          )}

          {poateGestiona && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={comutaScenariul}
                disabled={ocupat}
                style={{
                  border: '1px solid ' + C.primar, color: scenariu.pornit ? C.primar : '#fff',
                  background: scenariu.pornit ? '#fff' : C.primar, borderRadius: 8, padding: '8px 14px',
                  fontSize: 13, fontWeight: 600, cursor: ocupat ? 'default' : 'pointer', opacity: ocupat ? 0.6 : 1,
                }}
              >
                {scenariu.pornit ? t('inboxv3', 'botOpresteAici') : t('inboxv3', 'botPornesteAici')}
              </button>
              <button
                type="button"
                onClick={() => setFormular(true)}
                style={{ border: '1px solid ' + C.chenar, color: C.text, background: '#fff', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
              >
                {t('inboxv3', 'botSchimba')}
              </button>
            </div>
          )}

          {eSuperAdmin && (
            <div>
              <button
                type="button"
                onClick={comutaOprirea}
                disabled={ocupat}
                style={{
                  border: '1px solid ' + (oprit ? C.primar : C.alarma), color: oprit ? C.primar : C.alarma,
                  background: '#fff', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600,
                  cursor: ocupat ? 'default' : 'pointer', opacity: ocupat ? 0.6 : 1,
                }}
              >
                {oprit ? t('inboxv3', 'botRepornestePagina') : t('inboxv3', 'botOprestePagina')}
              </button>
              <div style={{ color: C.sters, marginTop: 6 }}>{t('inboxv3', 'botOprireExplicatie')}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PanouPostare({ card, onInchide, cuAI = false, poateRaspunde = true, rol = null, poateBot = false, filtruInitial = 'toate' }) {
  const [comentarii, setComentarii] = useState([]);
  const [seIncarca, setSeIncarca] = useState(true);
  const [inLucru, setInLucru] = useState(null);
  const [pozaClient, setPozaClient] = useState(null);
  const [filtru, setFiltru] = useState(filtruInitial || 'toate');

  // Poza clientului e deja la noi, copiata in depozitul nostru la conectarea contului
  // (`_shared/poza_cont.ts`, 16 septembrie 2026). Nu cere nicio aprobare — e poza propriei
  // pagini. `conturi_client` o da fara sa scoata tokenul din server.
  useEffect(() => {
    let anulat = false;
    (async () => {
      const { data } = await supabase.rpc('conturi_client', { p_client_id: card.client_id });
      if (anulat || !Array.isArray(data)) return;
      const cont = data.find(x => x.platform === (card.retea || 'facebook'));
      if (cont && cont.poza_url) setPozaClient(cont.poza_url);
    })();
    return () => { anulat = true; };
  }, [card.client_id, card.retea]);

  const incarcaComentarii = useCallback(async () => {
    const { data } = await supabase
      .from('mesaje')
      .select('id, external_id, parinte_id, text, autor, autor_poza, media_url, media_tip, retea, sters_de_autor, reactii, raspunsuri, ascuns, like_dat, arhivat, al_paginii, scris_la, raspuns_trimis_la, raspuns_text, raspuns_privat_la, registru, categorie, de_recitit, fapta_propusa, triaj_la, propunere')
      .eq('postare_id', card.postare_id)
      .eq('tip', 'comentariu')
      .order('reactii', { ascending: false, nullsFirst: false })
      .order('scris_la', { ascending: false })
      .limit(500);
    setComentarii(data || []);
    setSeIncarca(false);
  }, [card.postare_id]);

  useEffect(() => { incarcaComentarii(); }, [incarcaComentarii]);

  // Grila se reaplica singura la deschidere, peste etichetele deja puse. Nu cheama modelul si nu
  // costa nimic — dar tine faptele la zi cu pragul, si cu orice schimbare de grila de dupa triaj.
  // Fara asta, un triaj facut ieri ramanea cu regulile de ieri, iar cifra de pe buton mintea
  // (vazut pe viu, 18 septembrie: „Propune raspunsuri (3)" la un prag care cerea 10).
  const [grilaAliniata, setGrilaAliniata] = useState(false);
  useEffect(() => {
    if (!cuAI || grilaAliniata || seIncarca || !comentarii.some(c => c.triaj_la)) return;
    setGrilaAliniata(true);
    (async () => {
      const { data } = await supabase.functions.invoke('inbox-ai', {
        body: { postare_id: card.postare_id, mod: 'regrila' },
      });
      if (data && data.lucrate) await incarcaComentarii();
    })();
  }, [cuAI, grilaAliniata, seIncarca, comentarii, card.postare_id, incarcaComentarii]);

  function actualizeaza(id, schimbari) {
    setComentarii(prev => prev.map(c => (c.id === id ? { ...c, ...schimbari } : c)));
  }

  // Toate faptele trec pe aici. Fara dialog de confirmare: apesi, se intampla. Ce nu merge se
  // spune pe fata, cu motivul de la Facebook, nu cu un „a esuat" generic.
  // „Sterge tot despre acest om" si de pe comentariu (14 octombrie 2026, cerut de ea dupa proba):
  // omul care doar a comentat n-are conversatie in Messenger, deci n-avea de unde fi sters.
  // Aceeasi functie ca pe conversatie (`bot-sterge-om`), cu identificatorul autorului.
  const poateStergeOmul = rol === 'super_admin' || rol === 'client';
  async function fapta(c, ce) {
    if (inLucru) return;
    if (ce === 'sterge_omul' && !window.confirm(t('inboxv3', 'stergeOmulConfirm', { nume: c.autor || '' }))) return;
    setInLucru(c.id + ':' + ce);
    try {
      if (ce === 'sterge_omul') {
        const { data, error } = await supabase.functions.invoke('bot-sterge-om', {
          body: { client_id: card.client_id, psid: c.autor_id },
        });
        if (error) throw new Error(await detaliuEroare(error));
        setComentarii(prev => prev.filter(x => x.autor_id !== c.autor_id));
        alert(t('inboxv3', 'stergeOmulGata', { mesaje: data.mesaje || 0, apasari: data.apasari || 0 }));
        return;
      }

      // Instagram are alta functie si alt fel de a cere faptele. Ce nu se poate acolo (like,
      // raspuns privat, editare) nici nu ajunge pana aici: butoanele lipsesc din ecran.
      if (c.retea === 'instagram') {
        const corp = ce === 'ascunde' ? { mesaj_id: c.id, ascunde: true }
          : ce === 'arata' ? { mesaj_id: c.id, arata: true }
          : ce === 'sterge' ? { mesaj_id: c.id, sterge: true }
          : null;
        if (!corp) throw new Error(t('inboxv3', 'nuSePoateInstagram'));
        const { error } = await supabase.functions.invoke('instagram-reply', { body: corp });
        if (error) throw new Error(await detaliuEroare(error));
        if (ce === 'sterge') setComentarii(prev => prev.filter(x => x.id !== c.id));
        else actualizeaza(c.id, { ascuns: ce === 'ascunde' });
        return;
      }

      if (ce === 'like' || ce === 'unlike') {
        const { error } = await supabase.functions.invoke('facebook-like', {
          body: { mesaj_id: c.id, like: ce === 'like' },
        });
        if (error) throw new Error(await detaliuEroare(error));
        actualizeaza(c.id, { like_dat: ce === 'like' });
        return;
      }

      if (ce === 'ascunde' || ce === 'arata' || ce === 'sterge') {
        const { error } = await supabase.functions.invoke('facebook-moderate', {
          body: { mesaj_id: c.id, actiune: ce },
        });
        if (error) throw new Error(await detaliuEroare(error));
        if (ce === 'sterge') setComentarii(prev => prev.filter(x => x.id !== c.id));
        else actualizeaza(c.id, { ascuns: ce === 'ascunde' });
        return;
      }
    } catch (err) {
      alert(t('inboxv3', 'nuSAPutut', { eroare: err.message }));
    } finally {
      setInLucru(null);
    }
  }

  async function trimiteRaspuns(c, textRaspuns, felul = 'public') {
    if (inLucru || !textRaspuns.trim()) return false;
    setInLucru(c.id + ':raspuns');
    try {
      const { error } = c.retea === 'instagram'
        ? await supabase.functions.invoke('instagram-reply', {
            body: { mesaj_id: c.id, raspuns_text: textRaspuns },
          })
        : await supabase.functions.invoke('facebook-reply', {
            body: { mesaj_id: c.id, raspuns_text: textRaspuns, mod: felul },
          });
      if (error) throw new Error(await detaliuEroare(error));
      if (felul === 'privat') actualizeaza(c.id, { raspuns_privat_la: new Date().toISOString() });
      else if (felul === 'editare') actualizeaza(c.id, { text: textRaspuns });
      else actualizeaza(c.id, { raspuns_trimis_la: new Date().toISOString(), raspuns_text: textRaspuns });
      return true;
    } catch (err) {
      alert(t('inboxv3',
        felul === 'privat' ? 'privatNeplecat' : (felul === 'editare' ? 'editareaNefacuta' : 'raspunsNeplecat'),
        { eroare: err.message }));
      return false;
    } finally {
      setInLucru(null);
    }
  }

  // Un comentariu e „atins" daca s-a intamplat ceva cu el — acelasi inteles ca bifa din stanga.
  const estAtins = (c) => !!(c.raspuns_trimis_la || c.raspuns_privat_la || c.like_dat || c.ascuns
    || comentarii.some(x => x.al_paginii && x.parinte_id === c.external_id));

  // Firul: Facebook are doar doua niveluri, deci raspunsurile se aseaza sub parintele lor.
  // Cele al caror parinte nu e la noi raman la sfarsit, ca sa nu dispara de pe ecran.
  const capete = comentarii.filter(c => !c.parinte_id);
  const dupaParinte = {};
  const orfane = [];
  const idCunoscut = new Set(comentarii.map(c => c.external_id));
  comentarii.filter(c => c.parinte_id).forEach(c => {
    if (idCunoscut.has(c.parinte_id)) (dupaParinte[c.parinte_id] = dupaParinte[c.parinte_id] || []).push(c);
    else orfane.push(c);
  });

  return (
    <div
      onClick={onInchide}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,61,34,0.35)', display: 'flex', justifyContent: 'flex-end', zIndex: 60 }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ width: 'min(680px, 100%)', background: '#fff', height: '100%', overflowY: 'auto', padding: '22px 24px' }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 6 }}>
          <h2 style={{ margin: 0, fontFamily: "'DM Serif Display', Georgia, serif", fontSize: 22, color: C.inchis }}>
            {card.client_nume}
          </h2>
          <button onClick={onInchide} style={{ marginLeft: 'auto', border: 'none', background: 'transparent', fontSize: 22, cursor: 'pointer', color: C.sters }}>×</button>
        </div>

        <div style={{ fontSize: 12, color: C.sters, marginBottom: 12 }}>{dataFrumoasa(card.publicat_la)}</div>

        <div style={{ background: C.mentol, borderRadius: 10, padding: 14, fontSize: 14, color: C.text, lineHeight: 1.5, marginBottom: 16 }}>
          {card.text_postare || t('inboxv3', 'postareFaraText')}
        </div>

        <div style={{ display: 'flex', gap: 18, fontSize: 13, color: C.sters, marginBottom: 16, flexWrap: 'wrap' }}>
          {/* Cifra retelei, nu a noastra. Pe Facebook ea SCADE cand ascunzi un comentariu: un
              comentariu ascuns nu mai e numarat in totalul postarii, desi la noi ramane. */}
          <span>
            <strong style={{ color: C.text }}>
              {card.comentarii_total != null ? card.comentarii_total : comentarii.length}
            </strong>{' '}
            {t('inboxv3', 'pePlatforma')} <span style={{ display: 'inline-flex', verticalAlign: 'text-bottom' }}>
              <IconRetea id={card.retea || 'facebook'} size={13} />
            </span>{' '}{card.retea === 'instagram' ? 'Instagram' : 'Facebook'}
          </span>
          <span><strong style={{ color: C.text }}>{comentarii.length}</strong> {t('inboxv3', 'laNoi')}</span>
          <span><strong style={{ color: C.text }}>{Number(card.reactii_total || 0)}</strong> {t('inboxv3', 'reactiiLaComentarii')}</span>
          <span><strong style={{ color: C.text }}>{Number(card.ai_raspuns || 0)}</strong> {t('inboxv3', 'cifraAiRaspuns')}</span>
          <span style={{ color: C.sters }}>
            {t('inboxv3', 'dinCare')} <strong style={{ color: C.text }}>{Number(card.cu_raspuns_pagina || 0)}</strong> {t('inboxv3', 'cuText')}
            {' · '}<strong style={{ color: C.text }}>{Number(card.cu_like || 0)}</strong> {t('inboxv3', 'cuLike')}
          </span>
          {Number(card.noi || 0) > 0 && <span style={{ color: C.primar }}><strong>{card.noi}</strong> {t('inboxv3', 'noiDeLaUltimaVizita')}</span>}
        </div>

        {!seIncarca && card.retea !== 'instagram' && <BandaBot card={card} comentarii={comentarii} rol={rol} poateBot={poateBot} />}

        {!seIncarca && cuAI && comentarii.length > 0 && (
          <BaraAI
            card={card}
            comentarii={comentarii}
            atinsFn={estAtins}
            onGata={incarcaComentarii}
            inFiltru={filtru === 'toate' ? null : comentarii.filter(c => !c.al_paginii && potrivesteFiltrul(c, FILTRE.find(f => f.id === filtru) || FILTRE[0], estAtins))}
            numeFiltru={t('inboxv3', (FILTRE.find(f => f.id === filtru) || {}).eticheta || 'filtruToate')}
            onFiltru={setFiltru}
            idFiltru={filtru}
          />
        )}

        {/* Fara AI raman filtrele care nu cer niciun model: „Toate", „Ascunse acum", „Rămase". */}
        {!seIncarca && (cuAI ? comentarii.some(c => c.triaj_la) : comentarii.length > 0) && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
            {FILTRE.filter(f => cuAI || ['toate', 'ascunse', 'fara_semn'].includes(f.id)).map(f => {
              const n = comentarii.filter(c => !c.al_paginii && potrivesteFiltrul(c, f, estAtins)).length;
              if (!n && f.id !== 'toate') return null;
              return (
                <Cip
                  key={f.id}
                  eticheta={t('inboxv3', f.eticheta)}
                  numar={n}
                  activ={filtru === f.id}
                  culoare={f.id === 'la_tine' ? C.alarma : (f.id === 'de_ascuns' ? '#8a6d3b' : null)}
                  onClick={() => setFiltru(f.id)}
                />
              );
            })}
          </div>
        )}

        {seIncarca && <div style={{ color: C.sters }}>{t('inboxv3', 'seIncarcaComentarii')}</div>}
        {!seIncarca && !comentarii.length && <div style={{ color: C.sters }}>{t('inboxv3', 'niciunComentariu')}</div>}

        {capete.filter(c => potrivesteFiltrul(c, FILTRE.find(f => f.id === filtru) || FILTRE[0], estAtins)).map(c => (
          <Comentariu
            key={c.id}
            c={c}
            raspunsuri={dupaParinte[c.external_id] || []}
            inLucru={inLucru}
            onFapta={fapta}
            onRaspunde={trimiteRaspuns}
            clientNume={card.client_nume}
            clientPoza={pozaClient}
            poateRaspunde={poateRaspunde}
            poateStergeOmul={poateStergeOmul}
          />
        ))}

        {orfane.length > 0 && (
          <>
            <div style={{ marginTop: 18, marginBottom: 8, fontSize: 12, color: C.sters }}>
              {t('inboxv3', 'orfane')}
            </div>
            {orfane.map(c => (
              <Comentariu key={c.id} c={c} raspunsuri={[]} inLucru={inLucru} onFapta={fapta} onRaspunde={trimiteRaspuns} clientNume={card.client_nume} clientPoza={pozaClient} poateRaspunde={poateRaspunde} poateStergeOmul={poateStergeOmul} />
            ))}
          </>
        )}
      </div>
    </div>
  );
}

function Actiune({ eticheta, onClick, activa = false, ocupat = false, rosie = false }) {
  return (
    <button
      onClick={onClick}
      disabled={ocupat}
      style={{
        border: '1px solid ' + (rosie ? '#E6C3C0' : (activa ? C.primar : C.chenar)),
        background: activa ? C.mentol : '#fff',
        color: rosie ? C.alarma : (activa ? C.primar : C.text),
        borderRadius: 999, padding: '4px 11px', fontSize: 12,
        cursor: ocupat ? 'default' : 'pointer', opacity: ocupat ? 0.5 : 1, font: 'inherit',
        fontFamily: 'inherit', lineHeight: 1.6,
      }}
    >
      {eticheta}
    </button>
  );
}

// Fereastra in care Facebook livreaza un mesaj privat pornit dintr-un comentariu.
const ZILE_RASPUNS_PRIVAT = 7;

function Comentariu({ c, raspunsuri, inLucru, onFapta, onRaspunde, clientNume = '', clientPoza = null, indentat = false, poateRaspunde = true, poateStergeOmul = false }) {
  // `scrie` spune si CE se scrie: 'public', 'privat' sau 'editare'. Aceeasi caseta, alt drum.
  const [scrie, setScrie] = useState(null);
  const [text, setText] = useState('');
  const ocupat = !!inLucru && inLucru.startsWith(c.id + ':');

  // Semnul din stanga, ca sa nu te mai uiti de doua ori la acelasi rand. Nu se apasa nimic: se
  // deduce din ce s-a intamplat deja — raspuns, mesaj privat, like, ascundere, sau un raspuns al
  // clientului dedesubt. Butonul „Rezolvat" cerea o apasare in plus ca sa-i spui aplicatiei ceva
  // ce stia deja (scos la cererea ei, 18 septembrie 2026).
  const areRaspunsDeLaNoi = (raspunsuri || []).some(r => r.al_paginii);
  const atins = !!(c.raspuns_trimis_la || c.raspuns_privat_la || c.like_dat || c.ascuns || areRaspunsDeLaNoi);
  const deCeAtins = [
    c.raspuns_trimis_la || areRaspunsDeLaNoi ? t('inboxv3', 'semnRaspuns') : null,
    c.raspuns_privat_la ? t('inboxv3', 'semnPrivat') : null,
    c.like_dat ? t('inboxv3', 'semnLike') : null,
    c.ascuns ? t('inboxv3', 'semnAscuns') : null,
  ].filter(Boolean).join(' · ');

  const zileDeLaComentariu = c.scris_la
    ? (Date.now() - new Date(c.scris_la).getTime()) / 86400000
    : 0;
  // Butonul apare doar cand fapta chiar se poate: o singura data per comentariu, in 7 zile, si
  // niciodata pe ce a scris pagina (n-are sens sa-ti trimiti tie un mesaj privat).
  const eInstagram = c.retea === 'instagram';
  // Ce nu se poate pe Instagram nu se arata: like-ul cere `instagram_manage_engagement`
  // (necerută niciodată), raspunsul privat cere permisiunea de mesaje (sta pe „Ready for
  // testing"), iar comentariile Instagram nu se pot edita deloc, prin nicio permisiune.
  const poatePrivat = !eInstagram && !c.al_paginii && !c.raspuns_privat_la
    && zileDeLaComentariu <= ZILE_RASPUNS_PRIVAT;

  function deschideCaseta(felul) {
    setText(felul === 'editare' ? (c.text || '') : '');
    setScrie(felul);
  }

  async function trimite() {
    const reusit = await onRaspunde(c, text, scrie || 'public');
    if (reusit) { setText(''); setScrie(null); }
  }

  return (
    <div style={{
      marginLeft: indentat ? 26 : 0, marginBottom: 12, paddingLeft: indentat ? 12 : 0,
      borderLeft: indentat ? '2px solid ' + C.chenar : 'none', opacity: c.arhivat ? 0.55 : 1,
    }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <div style={{ minWidth: 40, textAlign: 'right' }}>
          <div style={{ fontSize: 16, fontWeight: 600, color: Number(c.reactii) >= 50 ? C.primar : C.text }}>
            {c.reactii != null ? c.reactii : '–'}
          </div>
          <div style={{ fontSize: 10, color: C.sters }}>{t('inboxv3', 'cifraReactii')}</div>
          {atins && (
            <div
              title={t('inboxv3', 'aiFacutCeva', { ce: deCeAtins })}
              style={{
                marginTop: 6, marginLeft: 'auto', width: 20, height: 20, borderRadius: '50%',
                background: C.primar, color: '#fff', fontSize: 12, lineHeight: '20px',
                textAlign: 'center',
              }}
            >
              ✓
            </div>
          )}
        </div>

        <div style={{
          flex: 1, borderRadius: 10, padding: '10px 12px',
          background: c.al_paginii ? C.mentol : '#fff',
          border: '1px solid ' + (c.al_paginii ? C.primar : C.chenar),
        }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 11, color: C.sters, marginBottom: 4, flexWrap: 'wrap' }}>
            {/* Ce a scris clientul se vede dintr-o privire: e vocea lui pe pagina lui. */}
            {/* Pe randul clientului scrie NUMELE lui, nu „raspunsul clientului": pe ecran te uiti
                la o conversatie intre oameni, nu la o schema (cerut de ea, 18 septembrie). */}
            {c.al_paginii
              ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: C.primar, fontWeight: 600 }}>
                  <Avatar nume={clientNume} poza={clientPoza} marime={18} />
                  {clientNume || c.autor || t('inboxv3', 'clientul')}
                </span>
              : <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <Avatar nume={numeAutor(c.autor)} poza={c.autor_poza} marime={18} />
                  {numeAutor(c.autor)}
                </span>}
            <span>·</span>
            <span>{dataFrumoasa(c.scris_la)}</span>
            {c.ascuns && <span style={{ color: C.alarma, border: '1px solid ' + C.alarma, borderRadius: 999, padding: '0 6px' }}>{t('inboxv3', 'semnAscuns')}</span>}
            {c.categorie && <span style={{ background: C.mentol, color: C.primar, borderRadius: 999, padding: '0 7px' }}>{c.categorie.replace('_', ' ')}</span>}
            {c.registru && c.registru !== 'NIMIC' && (
              <span title={t('inboxv3', 'titluRegistru')} style={{ background: '#FDECEA', color: C.alarma, borderRadius: 999, padding: '0 7px' }}>{c.registru}</span>
            )}
            {c.de_recitit && <span style={{ background: '#FFF4E5', color: '#8a6d3b', borderRadius: 999, padding: '0 7px' }}>{t('inboxv3', 'etichetaDeRecitit')}</span>}
            {c.sters_de_autor && <span style={{ background: '#F4F5F4', color: C.sters, borderRadius: 999, padding: '0 7px' }}>{t('inboxv3', 'etichetaStersDeAutor')}</span>}
            {c.raspuns_trimis_la && <span style={{ color: C.primar }}>{t('inboxv3', 'etichetaAmRaspuns')}</span>}
            {c.raspuns_privat_la && <span style={{ color: C.primar }}>{t('inboxv3', 'etichetaPrivat')}</span>}
          </div>

          <div style={{
            fontSize: 14, lineHeight: 1.45, whiteSpace: 'pre-wrap',
            color: c.sters_de_autor ? C.sters : C.text,
            textDecoration: c.sters_de_autor ? 'line-through' : 'none',
          }}>{c.text}</div>

          {/* Poza sau abtibildul din comentariu (22 septembrie 2026). Culegerea le salva de
              mult in `media_url`/`media_tip`, dar ecranul nu le cerea deloc, asa ca un
              comentariu facut DOAR dintr-un abtibild arata gol. Gasit de Luminita pe o inima
              pusa sub o postare a Gagai. Adresa vine de la Meta si expira in cateva zile:
              cand moare, `onError` scoate imaginea si ramane textul, fara chenar spart. */}
          {c.media_url && (
            <img
              src={c.media_url}
              alt=""
              loading="lazy"
              onError={e => { e.currentTarget.style.display = 'none'; }}
              style={{ display: 'block', marginTop: 8, maxWidth: 180, maxHeight: 180, borderRadius: 10, border: '1px solid ' + C.chenar }}
            />
          )}

          {/* Ce A PLECAT deja pe Facebook. Se vede altfel decat o ciorna: fundal plin, bifa, si data
              la care a fost trimis. Pana pe 18 septembrie amandoua scriau „Raspunsul nostru:" si
              aratau la fel, ceea ce facea imposibil de stiut daca omul a primit raspunsul. */}
          {/* Nu si cand raspunsul paginii, adus inapoi de pe Facebook, sta deja dedesubt: ar fi
              acelasi text de doua ori (vazut de ea pe 15 oct 2026, crezand ca a raspuns de doua ori). */}
          {c.raspuns_text && !raspunsuri.some(r => r.al_paginii && String(r.text || '').trim() === String(c.raspuns_text).trim()) && (
            <div style={{
              marginTop: 10, padding: '10px 12px', borderRadius: 8,
              background: C.mentol, border: '1px solid ' + C.primar,
            }}>
              {/* Odata plecat, raspunsul nu mai e „al nostru": apare public sub numele clientului si
                  cu poza lui. Agentia nu exista acolo (spus de ea, 18 septembrie). */}
              <div style={{ fontSize: 11, color: C.primar, marginBottom: 4, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5 }}>
                <Avatar nume={clientNume} poza={clientPoza} marime={16} />
                {t('inboxv3', 'raspunsulLui', { client: clientNume || t('inboxv3', 'clientul') })}
                {c.raspuns_trimis_la ? t('inboxv3', 'trimisLa', { data: dataFrumoasa(c.raspuns_trimis_la) }) : ''}
              </div>
              <div style={{ fontSize: 14, color: C.text, lineHeight: 1.45, whiteSpace: 'pre-wrap' }}>{c.raspuns_text}</div>
            </div>
          )}

          {/* Propunerea sta ca bloc distinct, nu turnata direct in caseta: o citesti, apoi hotarasti.
              „(nu raspund)" e un raspuns bun, nu o eroare — vezi reteta. */}
          {c.propunere && !scrie && !c.raspuns_trimis_la && (
            <div style={{
              marginTop: 10, padding: '10px 12px', borderRadius: 8,
              background: c.propunere.startsWith('(nu răspund)') ? '#F4F5F4' : '#F2F8F4',
              border: '1px dashed ' + (c.propunere.startsWith('(nu răspund)') ? C.chenar : C.primar),
            }}>
              <div style={{ fontSize: 11, color: C.sters, marginBottom: 4, fontWeight: 600 }}>
                {c.propunere.startsWith('(nu răspund)')
                  ? t('inboxv3', 'propunereNuRaspunde')
                  : t('inboxv3', 'propunereCiorna', { client: clientNume || t('inboxv3', 'clientul') })}
              </div>
              <div style={{ fontSize: 14, color: C.text, lineHeight: 1.45, whiteSpace: 'pre-wrap' }}>{c.propunere}</div>
              {!c.propunere.startsWith('(nu răspund)') && (
                <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                  <button
                    onClick={() => { setText(c.propunere); setScrie('public'); }}
                    style={{
                      border: 'none', background: C.primar, color: '#fff', borderRadius: 999,
                      padding: '5px 14px', fontSize: 12, fontFamily: 'inherit', cursor: 'pointer',
                    }}
                  >
                    {t('inboxv3', 'folosestePropunerea')}
                  </button>
                </div>
              )}
            </div>
          )}

          {scrie && (
            <div style={{ marginTop: 10 }}>
              {scrie === 'privat' && (
                <div style={{ fontSize: 11, color: C.sters, marginBottom: 4 }}>
                  {t('inboxv3', 'explicatiePrivat')}
                </div>
              )}
              <textarea
                value={text}
                onChange={e => setText(e.target.value)}
                rows={3}
                placeholder={t('inboxv3', scrie === 'privat' ? 'scrieMesajulPrivat' : (scrie === 'editare' ? 'schimbaTextul' : 'scrieRaspunsul'))}
                style={{
                  width: '100%', boxSizing: 'border-box', border: '1px solid ' + C.chenar,
                  borderRadius: 8, padding: 10, fontSize: 14, fontFamily: 'inherit', resize: 'vertical',
                }}
              />
              <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                <button
                  onClick={trimite}
                  disabled={ocupat || !text.trim()}
                  style={{
                    border: 'none', background: C.primar, color: '#fff', borderRadius: 999,
                    padding: '6px 16px', fontSize: 13, fontFamily: 'inherit',
                    cursor: ocupat || !text.trim() ? 'default' : 'pointer',
                    opacity: ocupat || !text.trim() ? 0.5 : 1,
                  }}
                >
                  {ocupat
                    ? t('inboxv3', 'seTrimite')
                    : t('inboxv3', scrie === 'editare' ? 'salveazaTextul' : (scrie === 'privat' ? 'trimiteInPrivat' : 'trimiteRaspunsul'))}
                </button>
                <button
                  onClick={() => { setScrie(null); setText(''); }}
                  style={{ border: 'none', background: 'transparent', color: C.sters, fontSize: 13, fontFamily: 'inherit', cursor: 'pointer' }}
                >
                  {t('inboxv3', 'renunta')}
                </button>
              </div>
            </div>
          )}

          {/* Faptele care ne leaga de Facebook. „Blocheaza" lipseste dinadins: cere id-ul
              autorului, care nu vine pana la aprobarea Business Asset User Profile Access,
              deci ar fi un buton care da eroare. Vezi INBOX_V3_PLAN.md §13.8. */}
          {c.sters_de_autor ? (
            <div style={{ marginTop: 8, fontSize: 12, color: C.sters }}>
              {t('inboxv3', 'nuMaiExista')}
            </div>
          ) : poateRaspunde ? (
          <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
            {!scrie && !c.al_paginii && <Actiune eticheta={t('inboxv3', 'actiuneRaspunde')} onClick={() => deschideCaseta('public')} ocupat={ocupat} />}
            {!scrie && poatePrivat && <Actiune eticheta={t('inboxv3', 'actiunePrivat')} onClick={() => deschideCaseta('privat')} ocupat={ocupat} />}
            {!scrie && c.al_paginii && !eInstagram && <Actiune eticheta={t('inboxv3', 'actiuneEditeaza')} onClick={() => deschideCaseta('editare')} ocupat={ocupat} />}
            {/* Un singur cuvant, ca pe Facebook: butonul se COLOREAZA cand like-ul e dat si se
                retrage la a doua apasare. Un al doilea text („Nu mai dau like") e zgomot pe un
                rand cu cinci butoane, si suna a traducere. */}
            {!eInstagram && (
              <Actiune
                eticheta={t('inboxv3', c.like_dat ? 'actiuneLikeDat' : 'actiuneLike')}
                activa={!!c.like_dat}
                ocupat={ocupat}
                onClick={() => onFapta(c, c.like_dat ? 'unlike' : 'like')}
              />
            )}
            <Actiune
              eticheta={t('inboxv3', c.ascuns ? 'actiuneArata' : 'actiuneAscunde')}
              activa={!!c.ascuns}
              ocupat={ocupat}
              onClick={() => onFapta(c, c.ascuns ? 'arata' : 'ascunde')}
            />
            <Actiune eticheta={t('inboxv3', 'actiuneSterge')} rosie ocupat={ocupat} onClick={() => onFapta(c, 'sterge')} />
            {poateStergeOmul && !c.al_paginii && c.autor_id && (
              <Actiune eticheta={t('inboxv3', 'actiuneStergeOmul')} rosie ocupat={ocupat} onClick={() => onFapta(c, 'sterge_omul')} />
            )}
          </div>
          ) : null}
        </div>
      </div>

      {raspunsuri.map(r => (
        <Comentariu key={r.id} c={r} raspunsuri={[]} inLucru={inLucru} onFapta={onFapta} onRaspunde={onRaspunde} clientNume={clientNume} clientPoza={clientPoza} indentat poateRaspunde={poateRaspunde} poateStergeOmul={poateStergeOmul} />
      ))}
    </div>
  );
}
